import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Code, Eye, Save, Copy, Check, Plus, Trash2, Edit2, 
  Menu as MenuIcon, Layout, Palette, Settings, Link as LinkIcon,
  ArrowLeft, ArrowRight, Files
} from 'lucide-react';
import MegaMenuColumnEditor from '@/components/ui/MegaMenuColumnEditor';
import MediaBrowser from '@/components/MediaBrowser';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// CardButton component with hover state
const CardButton = ({ text, bgColor, textColor, hoverBgColor, hoverTextColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      style={{
        backgroundColor: isHovered ? hoverBgColor : bgColor,
        color: isHovered ? hoverTextColor : textColor,
        padding: '10px 20px',
        borderRadius: '6px',
        fontWeight: '600',
        display: 'inline-block',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {text}
    </div>
  );
};

const NavigationBuilder = () => {
  const [menus, setMenus] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [categoryHierarchy, setCategoryHierarchy] = useState([]);
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [previewExpandedColumns, setPreviewExpandedColumns] = useState([]);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCopyStyleDialog, setShowCopyStyleDialog] = useState(false);
  const [showDeleteRowDialog, setShowDeleteRowDialog] = useState(false);
  const [showRemoveColumnDialog, setShowRemoveColumnDialog] = useState(false);
  const [deleteRowIndex, setDeleteRowIndex] = useState(null);
  const [removeColumnData, setRemoveColumnData] = useState(null); // {rowIndex, columnCount}
  const [showCustomItemDialog, setShowCustomItemDialog] = useState(false);
  const [showCategoryCardDialog, setShowCategoryCardDialog] = useState(false);
  const [editingCategoryCardId, setEditingCategoryCardId] = useState(null);
  const [newMenuName, setNewMenuName] = useState('');
  const [newMenuType, setNewMenuType] = useState('horizontal'); // horizontal or vertical
  const [deleteMenuId, setDeleteMenuId] = useState(null);
  const [copyStyleSourceId, setCopyStyleSourceId] = useState('');
  const [customItem, setCustomItem] = useState({ label: '', url: '', url_type: 'custom', category_id: null, is_button: false, position: 'left', icon: null });
  const [editingCustomItemIndex, setEditingCustomItemIndex] = useState(null);
  
  // Mega menu editor states
  const [showColumnEditor, setShowColumnEditor] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editingColIndex, setEditingColIndex] = useState(null);
  const [expandedMobileItems, setExpandedMobileItems] = useState(new Set());
  const [showAddRowDialog, setShowAddRowDialog] = useState(false);
  const [newRowColumns, setNewRowColumns] = useState(3);
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);
  const [mediaBrowserCallback, setMediaBrowserCallback] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateColumn, setDuplicateColumn] = useState(null);
  const [duplicateTargetRow, setDuplicateTargetRow] = useState(null);

  // Icon options for custom items
  const iconOptions = {
    user: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    ),
    cart: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="9" cy="21" r="1"></circle>
        <circle cx="20" cy="21" r="1"></circle>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
      </svg>
    ),
    tag: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
        <line x1="7" y1="7" x2="7.01" y2="7"></line>
      </svg>
    ),
    heart: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
    ),
    box: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
        <line x1="12" y1="22.08" x2="12" y2="12"></line>
      </svg>
    ),
    bell: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
      </svg>
    ),
    settings: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M12 1v6m0 6v6m5.2-13.2l-4.2 4.2m0 6l4.2 4.2M23 12h-6m-6 0H5m13.2 5.2l-4.2-4.2m0-6l4.2-4.2"></path>
      </svg>
    ),
    logout: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
        <polyline points="16 17 21 12 16 7"></polyline>
        <line x1="21" y1="12" x2="9" y2="12"></line>
      </svg>
    )
  };

  // Load Google Fonts for preview
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;600&family=Roboto:wght@400;600&family=Open+Sans:wght@400;600&family=Lato:wght@400;600&family=Montserrat:wght@400;600&family=Poppins:wght@400;600&family=Raleway:wght@400;600&family=Nunito:wght@400;600&family=Work+Sans:wght@400;600&family=Source+Sans+Pro:wght@400;600&family=Ubuntu:wght@400;600&family=Oswald:wght@400;600&family=Quicksand:wght@400;600&family=Playfair+Display:wght@400;600&family=Merriweather:wght@400;600&family=Lora:wght@400;600&family=PT+Serif:wght@400;600&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    fetchMenus();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedMenuId) {
      fetchMenu(selectedMenuId);
    }
  }, [selectedMenuId]);

  const fetchMenus = async () => {
    try {
      const response = await axiosInstance.get('/navigation/menus');
      setMenus(response.data);
      if (response.data.length > 0 && !selectedMenuId) {
        setSelectedMenuId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch menus:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenu = async (menuId) => {
    try {
      const response = await axiosInstance.get(`/navigation/menus/${menuId}`);
      let menuData = response.data;
      
      // Data migration: Convert old flat mega_menu_columns to row structure
      if (menuData.mega_menu_rows && Array.isArray(menuData.mega_menu_rows)) {
        // Check if first item is a column object (old format) instead of a row object
        if (menuData.mega_menu_rows.length > 0 && !menuData.mega_menu_rows[0].hasOwnProperty('columns')) {
          console.log('Migrating old column format to rows...');
          // It's the old flat column format, convert to rows
          const oldColumns = menuData.mega_menu_rows;
          menuData.mega_menu_rows = [{
            columns: oldColumns
          }];
          // Auto-save the migrated data
          try {
            await axiosInstance.put(`/navigation/menus/${menuId}`, {
              mega_menu_rows: menuData.mega_menu_rows
            });
            console.log('Migration saved successfully');
          } catch (err) {
            console.error('Failed to save migrated data:', err);
          }
        }
        
        // Data migration: Add auto_include_subcategories default to existing category_list columns
        let needsSave = false;
        menuData.mega_menu_rows.forEach(row => {
          if (row.columns) {
            row.columns.forEach(col => {
              if (col.type === 'category_list' && col.auto_include_subcategories === undefined) {
                col.auto_include_subcategories = true;
                needsSave = true;
              }
            });
          }
        });
        
        if (needsSave) {
          console.log('Setting default auto_include_subcategories...');
          try {
            await axiosInstance.put(`/navigation/menus/${menuId}`, {
              mega_menu_rows: menuData.mega_menu_rows
            });
            console.log('Auto-include defaults saved successfully');
          } catch (err) {
            console.error('Failed to save auto-include defaults:', err);
          }
        }
      }
      
      // Ensure auto_include_new_root_categories has a default value
      if (menuData.auto_include_new_root_categories === undefined) {
        menuData.auto_include_new_root_categories = false;
      }
      
      // Ensure category_auto_include_subcategories exists
      if (!menuData.category_auto_include_subcategories) {
        menuData.category_auto_include_subcategories = {};
      }
      
      // Ensure category_positions exists
      if (!menuData.category_positions) {
        menuData.category_positions = {};
      }
      
      // Ensure category_orders exists and initialize for categories without order
      if (!menuData.category_orders) {
        menuData.category_orders = {};
      }
      (menuData.included_categories || []).forEach((catId, index) => {
        if (menuData.category_orders[catId] === undefined) {
          menuData.category_orders[catId] = index;
        }
      });
      
      // Ensure custom items have order
      if (menuData.custom_items) {
        menuData.custom_items = menuData.custom_items.map((item, index) => ({
          ...item,
          order: item.order ?? index
        }));
      }
      
      // Ensure mega_menu_button has position and order field
      if (menuData.mega_menu_button) {
        if (!menuData.mega_menu_button.position) {
          menuData.mega_menu_button.position = 'left';
        }
        if (menuData.mega_menu_button.order === undefined) {
          menuData.mega_menu_button.order = 0;
        }
      }
      
      setCurrentMenu(menuData);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      toast.error('Failed to load menu');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories/hierarchy');
      setCategoryHierarchy(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleCreateMenu = async () => {
    if (!newMenuName.trim()) {
      toast.error('Please enter a menu name');
      return;
    }

    try {
      const response = await axiosInstance.post('/navigation/menus', {
        name: newMenuName,
        menu_type: newMenuType
      });
      setMenus([...menus, response.data]);
      setSelectedMenuId(response.data.id);
      setNewMenuName('');
      setNewMenuType('horizontal'); // Reset to default
      setShowCreateDialog(false);
      toast.success('Menu created successfully');
    } catch (error) {
      toast.error('Failed to create menu');
    }
  };

  const handleDeleteMenu = async () => {
    try {
      await axiosInstance.delete(`/navigation/menus/${deleteMenuId}`);
      setMenus(menus.filter(m => m.id !== deleteMenuId));
      if (selectedMenuId === deleteMenuId) {
        setSelectedMenuId(menus.length > 1 ? menus[0].id : null);
      }
      setShowDeleteDialog(false);
      setDeleteMenuId(null);
      toast.success('Menu deleted successfully');
    } catch (error) {
      toast.error('Failed to delete menu');
    }
  };

  const handleDuplicateMenu = async (menuId) => {
    try {
      const response = await axiosInstance.post(`/navigation/menus/${menuId}/duplicate`);
      setMenus([...menus, response.data]);
      setSelectedMenuId(response.data.id);
      toast.success('Menu duplicated successfully');
    } catch (error) {
      toast.error('Failed to duplicate menu');
    }
  };

  // Column manipulation functions
  const moveColumn = (rowIndex, colIndex, direction) => {
    const rows = [...(currentMenu.mega_menu_rows || [])];
    const row = rows[rowIndex];
    const newIndex = direction === 'left' ? colIndex - 1 : colIndex + 1;
    
    if (newIndex < 0 || newIndex >= row.columns.length) return;
    
    const [movedColumn] = row.columns.splice(colIndex, 1);
    row.columns.splice(newIndex, 0, movedColumn);
    
    setCurrentMenu({...currentMenu, mega_menu_rows: rows});
  };

  const handleDuplicateColumn = (rowIndex, colIndex) => {
    const column = currentMenu.mega_menu_rows[rowIndex].columns[colIndex];
    setDuplicateColumn({...column});
    setDuplicateTargetRow(rowIndex);
    setShowDuplicateDialog(true);
  };

  const confirmDuplicateColumn = () => {
    if (duplicateTargetRow === null || !duplicateColumn) return;
    
    const rows = [...(currentMenu.mega_menu_rows || [])];
    rows[duplicateTargetRow].columns.push({...duplicateColumn});
    
    setCurrentMenu({...currentMenu, mega_menu_rows: rows});
    setShowDuplicateDialog(false);
    setDuplicateColumn(null);
    setDuplicateTargetRow(null);
    toast.success('Column duplicated successfully');
  };

  const confirmDeleteRow = () => {
    if (deleteRowIndex === null) return;
    
    const rows = (currentMenu.mega_menu_rows || []).filter((_, i) => i !== deleteRowIndex);
    setCurrentMenu({...currentMenu, mega_menu_rows: rows});
    setShowDeleteRowDialog(false);
    setDeleteRowIndex(null);
    toast.success('Row deleted successfully');
  };

  const confirmRemoveColumn = () => {
    if (!removeColumnData) return;
    
    const { rowIndex, columnCount } = removeColumnData;
    if (columnCount > 1) {
      const rows = [...(currentMenu.mega_menu_rows || [])];
      rows[rowIndex].columns.pop();
      setCurrentMenu({...currentMenu, mega_menu_rows: rows});
      toast.success('Column removed successfully');
    }
    
    setShowRemoveColumnDialog(false);
    setRemoveColumnData(null);
  };

  const handleCopyStyle = async () => {
    if (!copyStyleSourceId) {
      toast.error('Please select a source menu');
      return;
    }

    try {
      await axiosInstance.post(`/navigation/menus/${selectedMenuId}/copy-style/${copyStyleSourceId}`);
      await fetchMenu(selectedMenuId);
      setShowCopyStyleDialog(false);
      setCopyStyleSourceId('');
      toast.success('Style copied successfully');
    } catch (error) {
      toast.error('Failed to copy style');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('Saving menu with data:', currentMenu);
      console.log('category_orders:', currentMenu.category_orders);
      console.log('mega_menu_button:', currentMenu.mega_menu_button);
      console.log('custom_items orders:', currentMenu.custom_items?.map(item => ({label: item.label, order: item.order})));
      
      await axiosInstance.put(`/navigation/menus/${selectedMenuId}`, currentMenu);
      toast.success('Menu saved successfully');
      await fetchMenus();
    } catch (error) {
      console.error('Save error:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to save menu');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCode = async () => {
    try {
      const response = await axiosInstance.get(`/navigation/menus/${selectedMenuId}/embed-code`);
      setEmbedCode(response.data.embed_code);
      toast.success('Embed code generated!');
    } catch (error) {
      toast.error('Failed to generate code');
    }
  };

  const handleCopyCode = () => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(embedCode)
        .then(() => {
          setCopied(true);
          toast.success('Code copied to clipboard!');
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          // Fallback to older method
          fallbackCopyToClipboard(embedCode);
        });
    } else {
      // Use fallback for older browsers or when clipboard API is blocked
      fallbackCopyToClipboard(embedCode);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopied(true);
      toast.success('Code copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy. Please copy manually.');
    }
    
    document.body.removeChild(textArea);
  };

  const toggleCategory = (categoryId) => {
    setCurrentMenu(prev => {
      const included = prev.included_categories.includes(categoryId);
      return {
        ...prev,
        included_categories: included
          ? prev.included_categories.filter(id => id !== categoryId)
          : [...prev.included_categories, categoryId]
      };
    });
  };

  const selectAllCategories = () => {
    const allCategoryIds = [];
    const collectIds = (categories) => {
      categories.forEach(cat => {
        allCategoryIds.push(cat.id);
        if (cat.children && cat.children.length > 0) {
          collectIds(cat.children);
        }
      });
    };
    collectIds(categoryHierarchy);
    
    setCurrentMenu(prev => ({
      ...prev,
      included_categories: allCategoryIds
    }));
  };

  const deselectAllCategories = () => {
    setCurrentMenu(prev => ({
      ...prev,
      included_categories: []
    }));
  };

  // Render arrow based on style
  const renderArrow = () => {
    const arrowStyle = currentMenu.arrow_style || 'chevron';
    
    switch(arrowStyle) {
      case 'chevron':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="14" height="14" style={{display: 'inline-block', verticalAlign: 'middle'}}>
            <path fill="currentColor" d="M38,8.064v10.452l-18,14.21L2,18.515V8.064l17.38,13.721L20,22.274l0.62-0.489L38,8.064 M39,6L20,21L1,6v13l19,15l19-15V6L39,6z"/>
          </svg>
        );
      case 'circled':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="16" height="16" style={{display: 'inline-block', verticalAlign: 'middle'}}>
            <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" strokeWidth="2"/>
            <path fill="currentColor" d="M9 17L12 14 20 22 28 14 31 17 20 28z"/>
          </svg>
        );
      case 'double':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="14" height="14" style={{display: 'inline-block', verticalAlign: 'middle'}}>
            <path fill="currentColor" d="M36.433,7.395l1.168,1.602L20,21.762L2.399,8.997l1.168-1.602l15.846,11.492L20,19.313l0.587-0.426L36.433,7.395 M36.653,6L20,18.078L3.347,6L1,9.218l19,13.779L39,9.218L36.653,6L36.653,6z"/>
            <path fill="currentColor" d="M36.433,18.391l1.169,1.603L20,32.765L2.399,19.994l1.169-1.603l15.845,11.497L20,30.314l0.587-0.426L36.433,18.391 M36.653,16.996L20,29.079L3.347,16.996L1,20.215L20,34l19-13.785L36.653,16.996L36.653,16.996z"/>
          </svg>
        );
      default:
        return '▼';
    }
  };

  // Get all menu items (categories + custom items + mega menu button) for placement management
  const getAllMenuItems = () => {
    const items = [];
    
    // Add mega menu button if enabled
    if (currentMenu.mega_menu_enabled) {
      const megaMenuPosition = currentMenu.mega_menu_button?.position || 'left';
      const megaMenuOrder = currentMenu.mega_menu_button?.order ?? 0;
      items.push({
        type: 'megamenu',
        id: 'megamenu-button',
        name: currentMenu.mega_menu_button?.label || 'Shop',
        position: megaMenuPosition,
        order: megaMenuOrder
      });
    }
    
    // Add categories - BUT NOT if mega menu is expanding categories type
    // For expanding categories, categories only show in the mega menu dropdown
    const isExpandingCategories = currentMenu.mega_menu_enabled && currentMenu.mega_menu_content_type === 'expanding_categories';
    if (!isExpandingCategories) {
      (currentMenu.included_categories || []).forEach((catId, index) => {
        const category = categoryHierarchy.find(c => c.id === catId);
        if (category) {
          const position = currentMenu.category_positions?.[catId] || 'left';
          const order = currentMenu.category_orders?.[catId] ?? index;
          items.push({
            type: 'category',
            id: catId,
            name: category.name,
            position,
            order,
            category // Include full category object for preview
          });
        }
      });
    }
    
    // Add custom items
    (currentMenu.custom_items || []).forEach((item, index) => {
      items.push({
        type: 'custom',
        id: `custom-${index}`,
        name: item.label || '(Icon only)',
        icon: item.icon,
        url: item.url,
        is_button: item.is_button,
        position: item.position || 'left',
        order: item.order ?? index,
        customIndex: index,
        customItemData: item // Include full custom item for preview
      });
    });
    
    // Sort by order within each position
    return items.sort((a, b) => a.order - b.order);
  };

  // Helper function to get card for a column in preview
  const getCardForColumn = (parentCategoryId) => {
    // Check if there's a category-specific card
    if (parentCategoryId && currentMenu.column_specific_cards && currentMenu.column_specific_cards[parentCategoryId]) {
      return currentMenu.column_specific_cards[parentCategoryId];
    }
    // Fall back to default card
    if (currentMenu.default_vertical_card) {
      return currentMenu.default_vertical_card;
    }
    return null;
  };

  // Recursive function to render nested category dropdowns in preview
  const renderNestedCategoryPreview = (category, includedSet, depth = 0) => {
    const visibleChildren = (category.children || []).filter(child => includedSet.has(child.id));
    
    if (visibleChildren.length === 0) {
      return (
        <div key={category.id} className="nav-preview-dropdown-item">
          {currentMenu.show_icons && '📄 '}
          {category.name}
        </div>
      );
    }
    
    return (
      <div key={category.id} className="nav-preview-dropdown-item" style={{position: 'relative'}}>
        {currentMenu.show_icons && (depth === 0 ? '📁 ' : '📄 ')}
        {category.name}
        {visibleChildren.length > 0 && ' ›'}
        {visibleChildren.length > 0 && (
          <div className="nav-preview-dropdown nav-preview-nested-dropdown">
            {visibleChildren.map((child) => renderNestedCategoryPreview(child, includedSet, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Get items for a specific position, sorted by order
  const getItemsForPosition = (position) => {
    return getAllMenuItems()
      .filter(item => item.position === position)
      .sort((a, b) => a.order - b.order);
  };

  // Render a single menu item in the preview
  const renderPreviewItem = (item) => {
    const isExpandingCategories = currentMenu.mega_menu_content_type === 'expanding_categories';
    const hasMegaMenuContent = (currentMenu.mega_menu_rows && currentMenu.mega_menu_rows.length > 0) || isExpandingCategories;
    
    if (item.type === 'megamenu' && hasMegaMenuContent) {
      return (
        <div 
          key="megamenu"
          className="nav-preview-item mega-menu-button"
          style={{
            color: currentMenu.text_color,
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
          onMouseEnter={() => {
            setIsMegaMenuOpen(true);
            // Show appropriate preview based on content type
            const selector = isExpandingCategories 
              ? '.mega-menu-preview.expanding-preview' 
              : '.mega-menu-preview:not(.expanding-preview)';
            const previewEl = document.querySelector(selector);
            if (previewEl) previewEl.style.display = 'block';
          }}
          onMouseLeave={() => {
            setIsMegaMenuOpen(false);
            // Hide appropriate preview based on content type
            const selector = isExpandingCategories 
              ? '.mega-menu-preview.expanding-preview' 
              : '.mega-menu-preview:not(.expanding-preview)';
            const previewEl = document.querySelector(selector);
            if (previewEl) previewEl.style.display = 'none';
          }}
        >
          {item.name}
          {(currentMenu.mega_menu_show_arrow !== false) && (
            <span className={`nav-preview-arrow ${isMegaMenuOpen ? 'mega-menu-open' : ''}`} style={{marginLeft: '6px'}}>
              {renderArrow()}
            </span>
          )}
        </div>
      );
    } else if (item.type === 'category' && item.category) {
      const cat = item.category;
      return (
        <div 
          key={cat.id}
          className="nav-preview-item"
          style={{
            color: currentMenu.text_color,
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {currentMenu.show_icons && '📂 '}
          {cat.name}
          {(() => {
            // Filter children to only show selected ones
            const includedSet = new Set(currentMenu.included_categories || []);
            const visibleChildren = (cat.children || []).filter(child => includedSet.has(child.id));
            
            return (
              <>
                {currentMenu.show_dropdown_arrows && visibleChildren.length > 0 && (
                  <span className="nav-preview-arrow" style={{marginLeft: '6px'}}>
                    {renderArrow()}
                  </span>
                )}
                {visibleChildren.length > 0 && (
                  <div className="nav-preview-dropdown">
                    {visibleChildren.map((child) => renderNestedCategoryPreview(child, includedSet, 0))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      );
    } else if (item.type === 'custom' && item.customItemData) {
      const customItem = item.customItemData;
      const IconComponent = customItem.icon ? iconOptions[customItem.icon] : null;
      return (
        <div 
          key={`custom-${item.customIndex}`}
          className="nav-preview-item"
          style={{
            color: customItem.is_button ? '#ffffff' : currentMenu.text_color,
            backgroundColor: customItem.is_button ? currentMenu.primary_color : 'transparent',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: customItem.is_button ? '600' : 'normal',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          {IconComponent && <IconComponent />}
          {customItem.label}
        </div>
      );
    }
    return null;
  };

  // Update category position
  const updateCategoryPosition = (categoryId, newPosition) => {
    setCurrentMenu(prev => ({
      ...prev,
      category_positions: {
        ...(prev.category_positions || {}),
        [categoryId]: newPosition
      }
    }));
  };

  // Update custom item position
  const updateCustomItemPosition = (customIndex, newPosition) => {
    console.log('Updating custom item', customIndex, 'to position:', newPosition);
    const updated = [...(currentMenu.custom_items || [])];
    updated[customIndex] = { ...updated[customIndex], position: newPosition };
    console.log('Updated custom items:', updated);
    setCurrentMenu({ ...currentMenu, custom_items: updated });
  };

  // Update mega menu button position
  const updateMegaMenuPosition = (newPosition) => {
    console.log('Updating mega menu position to:', newPosition);
    console.log('Current mega_menu_button:', currentMenu.mega_menu_button);
    setCurrentMenu(prev => {
      const updated = {
        ...prev,
        mega_menu_button: {
          ...(prev.mega_menu_button || {}),
          position: newPosition
        }
      };
      console.log('Updated mega_menu_button:', updated.mega_menu_button);
      return updated;
    });
  };

  // Move item within same position group
  const moveMenuItem = (item, direction, groupItems) => {
    console.log('moveMenuItem called:', {item, direction, groupItems});
    
    const currentIndex = groupItems.findIndex(i => {
      if (i.type === 'megamenu' && item.type === 'megamenu') return true;
      if (i.type === 'category' && item.type === 'category') return i.id === item.id;
      if (i.type === 'custom' && item.type === 'custom') return i.customIndex === item.customIndex;
      return false;
    });
    
    console.log('Current index:', currentIndex);
    
    if (currentIndex === -1) {
      console.log('Item not found in group');
      return;
    }
    
    // Find adjacent item to swap with (any type)
    let targetIndex = -1;
    if (direction === 'up' && currentIndex > 0) {
      targetIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < groupItems.length - 1) {
      targetIndex = currentIndex + 1;
    }
    
    if (targetIndex === -1) {
      console.log('Already at edge of list');
      return;
    }
    
    const targetItem = groupItems[targetIndex];
    console.log(`Moving ${direction}, swapping with:`, targetItem);
    swapMenuItems(item, targetItem, groupItems);
  };

  // Swap two items' orders using the order field
  const swapMenuItems = (item1, item2, groupItems) => {
    console.log('Swapping items:', item1, item2);
    
    // Swap their order values
    const order1 = item1.order;
    const order2 = item2.order;
    
    console.log(`Swapping orders: ${order1} <-> ${order2}`);
    
    // Update based on item types
    setCurrentMenu(prev => {
      const updated = { ...prev };
      
      // Update item1's order
      if (item1.type === 'megamenu') {
        updated.mega_menu_button = {
          ...(updated.mega_menu_button || {}),
          order: order2
        };
      } else if (item1.type === 'category') {
        updated.category_orders = {
          ...(updated.category_orders || {}),
          [item1.id]: order2
        };
      } else if (item1.type === 'custom') {
        updated.custom_items = [...(updated.custom_items || [])];
        updated.custom_items[item1.customIndex] = {
          ...updated.custom_items[item1.customIndex],
          order: order2
        };
      }
      
      // Update item2's order
      if (item2.type === 'megamenu') {
        updated.mega_menu_button = {
          ...(updated.mega_menu_button || {}),
          order: order1
        };
      } else if (item2.type === 'category') {
        updated.category_orders = {
          ...(updated.category_orders || {}),
          [item2.id]: order1
        };
      } else if (item2.type === 'custom') {
        if (!updated.custom_items) updated.custom_items = [...(prev.custom_items || [])];
        updated.custom_items[item2.customIndex] = {
          ...updated.custom_items[item2.customIndex],
          order: order1
        };
      }
      
      console.log('Updated menu state:', updated);
      return updated;
    });
  };

  const handleAddCustomItem = () => {
    if (!customItem.label && !customItem.icon) {
      toast.error('Please enter a label or select an icon');
      return;
    }
    
    if (!customItem.url) {
      toast.error('Please enter a URL');
      return;
    }

    const newItem = { 
      ...customItem,
      order: editingCustomItemIndex !== null ? currentMenu.custom_items[editingCustomItemIndex].order : (currentMenu.custom_items?.length || 0)
    };
    
    // Check if it's a mobile custom item
    if (customItem.position === 'mobile') {
      setCurrentMenu({ 
        ...currentMenu, 
        mobile_custom_items: [...(currentMenu.mobile_custom_items || []), newItem] 
      });
    } else {
      if (editingCustomItemIndex !== null) {
        const updated = [...currentMenu.custom_items];
        updated[editingCustomItemIndex] = newItem;
        setCurrentMenu({ ...currentMenu, custom_items: updated });
      } else {
        setCurrentMenu({ 
          ...currentMenu, 
          custom_items: [...(currentMenu.custom_items || []), newItem] 
        });
      }
    }

    setCustomItem({ label: '', url: '', url_type: 'custom', category_id: null, is_button: false, position: 'left', icon: null });
    setEditingCustomItemIndex(null);
    setShowCustomItemDialog(false);
  };

  const handleEditCustomItem = (index) => {
    setCustomItem(currentMenu.custom_items[index]);
    setEditingCustomItemIndex(index);
    setShowCustomItemDialog(true);
  };

  const handleDeleteCustomItem = (index) => {
    const updated = currentMenu.custom_items.filter((_, i) => i !== index);
    setCurrentMenu({ ...currentMenu, custom_items: updated });
  };

  const moveCustomItem = (index, direction) => {
    const items = [...currentMenu.custom_items];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= items.length) return;
    
    // Swap items
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    
    setCurrentMenu({ ...currentMenu, custom_items: items });
  };

  const renderLivePreview = () => {
    // Get selected categories with hierarchy
    const getSelectedCategories = (items) => {
      let result = [];
      items.forEach(item => {
        if (currentMenu.included_categories.includes(item.id)) {
          const cat = { ...item, children: [] };
          if (item.children && item.children.length > 0) {
            cat.children = getSelectedCategories(item.children);
          }
          result.push(cat);
        }
      });
      return result;
    };

    const selectedCategories = getSelectedCategories(categoryHierarchy);
    const customItems = currentMenu.custom_items || [];
    const hasMegaMenu = currentMenu.mega_menu_enabled && currentMenu.mega_menu_rows && currentMenu.mega_menu_rows.length > 0;

    if (selectedCategories.length === 0 && customItems.length === 0 && !hasMegaMenu) {
      return (
        <div className="p-12 text-center border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
          <Eye className="w-12 h-12 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-400">Select categories, add custom items, or configure mega menu to see preview</p>
        </div>
      );
    }

    const hasArrowAnimation = currentMenu.arrow_animation === 'rotate';

    return (
      <div className="border-2 border-slate-200 rounded-lg p-4 bg-white">
        <div className="flex items-center gap-2 mb-3">
          <Eye className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-600">Live Interactive Preview</span>
        </div>
        
        <style>{`
          .nav-preview-item {
            transition: all 0.3s ease;
            position: relative;
          }
          .nav-preview-item:hover {
            color: ${currentMenu.hover_color} !important;
            background-color: ${currentMenu.hover_color}15 !important;
          }
          .nav-preview-arrow {
            display: inline-block;
            transition: transform 0.3s ease;
            margin-left: 4px;
          }
          ${hasArrowAnimation ? `
          .nav-preview-item:hover .nav-preview-arrow {
            transform: rotate(180deg);
          }
          .nav-preview-arrow.mega-menu-open {
            transform: rotate(180deg) !important;
          }
          ` : ''}
          .nav-preview-dropdown {
            display: none;
            position: absolute;
            top: 100%;
            left: 0;
            min-width: 200px;
            background: ${currentMenu.dropdown_bg};
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            border-radius: 4px;
            padding: 8px;
            z-index: 1000;
            margin-top: 0;
            animation: ${currentMenu.animation_style === 'fade' ? 'fadeIn' : currentMenu.animation_style === 'slide' ? 'slideDown' : 'none'} 0.2s ease;
          }
          .nav-preview-item:hover > .nav-preview-dropdown {
            display: block;
          }
          .nav-preview-dropdown-item {
            padding: 8px 12px;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s ease;
            color: ${currentMenu.dropdown_text_color || currentMenu.text_color};
            position: relative;
          }
          .nav-preview-dropdown-item:hover {
            background-color: ${currentMenu.dropdown_hover_color || currentMenu.hover_color}20;
            color: ${currentMenu.dropdown_hover_color || currentMenu.hover_color};
          }
          .nav-preview-nested-dropdown {
            display: none !important;
            position: absolute;
            top: 0;
            left: 100%;
            min-width: 200px;
            background: ${currentMenu.dropdown_bg};
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            border-radius: 4px;
            padding: 8px;
            z-index: 1001;
            margin-left: -1px;
          }
          .nav-preview-dropdown-item:hover > .nav-preview-nested-dropdown {
            display: block !important;
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .mega-menu-preview {
            min-width: 600px !important;
            ${currentMenu.mega_menu_animation === 'fade' ? 'animation: megaMenuFadeIn 0.3s ease;' : ''}
            ${currentMenu.mega_menu_animation === 'slide' ? 'animation: megaMenuSlide 0.3s ease;' : ''}
            ${currentMenu.mega_menu_animation === 'fade-slide' ? 'animation: megaMenuFadeSlide 0.3s ease;' : ''}
          }
          @keyframes megaMenuFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes megaMenuSlide {
            from { transform: translateX(-50%) translateY(-10px); }
            to { transform: translateX(-50%) translateY(0); }
          }
          @keyframes megaMenuFadeSlide {
            from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
          .mega-menu-preview::before {
            content: '';
            position: absolute;
            top: -40px;
            left: 0;
            right: 0;
            height: 40px;
            background: transparent;
          }
          .mega-menu-preview:hover {
            display: block !important;
          }
          .nav-preview-wrapper:has(.mega-menu-button:hover) .mega-menu-preview {
            display: block !important;
          }
          .mega-menu-root-category {
            transition: all 0.3s ease;
            display: block !important;
          }
          .mega-menu-root-category:hover {
            color: ${currentMenu.mega_menu_root_hover || currentMenu.dropdown_hover_color} !important;
            background-color: ${currentMenu.mega_menu_root_hover_bg || (currentMenu.dropdown_hover_color + '15')} !important;
            ${currentMenu.mega_menu_hover_effect === 'underline' ? `text-decoration: underline;` : ''}
            ${currentMenu.mega_menu_hover_effect === 'bold' ? `font-weight: 700 !important;` : ''}
            ${currentMenu.mega_menu_hover_effect === 'scale' ? `transform: scale(1.02);` : ''}
          }
          .mega-menu-subcategory {
            transition: all 0.3s ease;
            display: flex !important;
          }
          .mega-menu-subcategory:hover {
            color: ${currentMenu.mega_menu_sub_hover || currentMenu.dropdown_hover_color} !important;
            background-color: ${currentMenu.mega_menu_sub_hover_bg || (currentMenu.dropdown_hover_color + '15')} !important;
            ${currentMenu.mega_menu_hover_effect === 'underline' ? `text-decoration: underline;` : ''}
            ${currentMenu.mega_menu_hover_effect === 'bold' ? `font-weight: 700 !important;` : ''}
            ${currentMenu.mega_menu_hover_effect === 'scale' ? `transform: scale(1.02);` : ''}
          }
          .nav-preview-wrapper {
            position: relative;
            overflow: visible;
            padding-bottom: 50px;
          }
        `}</style>

        {/* Vertical Menu Preview */}
        {currentMenu.menu_type === 'vertical' ? (
          <div className="vertical-menu-preview" style={{
            display: 'flex',
            gap: `${currentMenu.column_gap || 0}px`,
            fontFamily: currentMenu.font_family,
            fontSize: `${currentMenu.font_size}px`,
            backgroundColor: currentMenu.vertical_column_bg || '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: `${currentMenu.border_radius ?? 8}px`,
            overflow: 'hidden'
          }}>
            {/* First Column - Root Categories */}
            <div style={{
              width: currentMenu.column_width || '280px',
              borderRight: '1px solid #e2e8f0',
              padding: '12px 0'
            }}>
              {(currentMenu.included_categories || [])
                .map((catId, index) => ({ catId, order: currentMenu.category_orders?.[catId] ?? index }))
                .sort((a, b) => a.order - b.order)
                .map(({ catId }) => {
                const category = categoryHierarchy.find(c => c.id === catId);
                if (!category) return null;
                const hasChildren = category.children && category.children.length > 0;
                
                return (
                  <div
                    key={catId}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'transparent',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = currentMenu.vertical_hover_bg || '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span>{category.name}</span>
                    {hasChildren && <span>›</span>}
                  </div>
                );
              })}
            </div>
            
            {/* Placeholder for expanded columns */}
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94a3b8',
              padding: '40px',
              textAlign: 'center'
            }}>
              <div>
                <p className="text-sm">Click a category to expand columns</p>
                <p className="text-xs mt-2">(Interactive preview coming soon)</p>
              </div>
            </div>
          </div>
        ) : (
          // Horizontal Menu Preview
          <>

        {/* Wrapper with relative positioning for mega menu context */}
        <div className="nav-preview-wrapper">
          {/* Debug logging */}
          {console.log('Preview - mega_menu_button:', currentMenu.mega_menu_button)}
          {console.log('Preview - mega_menu_button.position:', currentMenu.mega_menu_button?.position)}
          
          <div 
            style={{
              fontFamily: currentMenu.font_family,
              fontSize: `${currentMenu.font_size}px`,
              backgroundColor: currentMenu.background_color,
              color: currentMenu.text_color,
              padding: `${currentMenu.padding_y ?? 12}px ${currentMenu.padding_x ?? 20}px`,
              borderRadius: `${currentMenu.border_radius ?? 8}px`,
              display: 'flex',
              flexDirection: currentMenu.layout === 'vertical' ? 'column' : 'row',
              gap: '8px',
              alignItems: currentMenu.layout === 'vertical' ? 'flex-start' : 'center'
            }}
            className="shadow-sm"
          >
          {/* Left side - Always render even if empty */}
          <div style={{
            display: 'flex',
            flexDirection: currentMenu.layout === 'vertical' ? 'column' : 'row',
            gap: '8px',
            alignItems: currentMenu.layout === 'vertical' ? 'flex-start' : 'center',
            flex: '1'
          }}>
            {getItemsForPosition('left').map(item => renderPreviewItem(item))}
          </div>

          {/* Center - Always render even if empty */}
          <div style={{
            display: 'flex',
            flexDirection: currentMenu.layout === 'vertical' ? 'column' : 'row',
            gap: '8px',
            alignItems: currentMenu.layout === 'vertical' ? 'flex-start' : 'center',
            flex: '1',
            justifyContent: 'center'
          }}>
            {getItemsForPosition('center').map(item => renderPreviewItem(item))}
          </div>

          {/* Right side - Always render even if empty */}
          <div style={{
            display: 'flex',
            flexDirection: currentMenu.layout === 'vertical' ? 'column' : 'row',
            gap: '8px',
            alignItems: currentMenu.layout === 'vertical' ? 'flex-start' : 'center',
            flex: '1',
            justifyContent: 'flex-end'
          }}>
            {getItemsForPosition('right').map(item => renderPreviewItem(item))}
          </div>
        </div>
        
        {/* Mega Menu Dropdown - positioned relative to wrapper */}
        {currentMenu.mega_menu_enabled && currentMenu.mega_menu_rows && currentMenu.mega_menu_rows.length > 0 && (
          <div 
            className="mega-menu-preview" 
            style={{
              display: 'none',
              position: 'absolute',
              width: '1200px',
              maxWidth: '85%',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: `${currentMenu.mega_menu_dropdown_padding !== undefined ? currentMenu.mega_menu_dropdown_padding : 32}px`,
              backgroundColor: currentMenu.mega_menu_dropdown_bg || currentMenu.dropdown_bg || '#ffffff',
              borderRadius: `${currentMenu.mega_menu_border_radius || 8}px`,
              boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
              zIndex: 9999,
              top: '100%',
              marginTop: '-52px',
              overflow: 'hidden'
            }}
            onMouseEnter={() => setIsMegaMenuOpen(true)}
            onMouseLeave={() => {
              setIsMegaMenuOpen(false);
              const previewEl = document.querySelector('.mega-menu-preview:not(.expanding-preview)');
              if (previewEl) previewEl.style.display = 'none';
            }}
          >
            {/* Render each row */}
            {currentMenu.mega_menu_rows.map((row, rowIndex) => (
              <div key={rowIndex} style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${(row.columns || []).length}, 1fr)`,
                gap: `${currentMenu.mega_menu_column_gap !== undefined ? currentMenu.mega_menu_column_gap : 32}px`,
                marginTop: rowIndex > 0 ? `${currentMenu.mega_menu_row_gap !== undefined ? currentMenu.mega_menu_row_gap : 32}px` : '0',
                paddingTop: rowIndex > 0 ? `${currentMenu.mega_menu_row_gap !== undefined ? currentMenu.mega_menu_row_gap : 32}px` : '0',
                borderTop: (rowIndex > 0 && currentMenu.mega_menu_row_divider !== false && (currentMenu.mega_menu_row_gap === undefined || currentMenu.mega_menu_row_gap > 0)) ? `${currentMenu.mega_menu_row_divider_size || 1}px solid ${currentMenu.mega_menu_row_divider_color || 'rgba(0,0,0,0.1)'}` : 'none'
              }}>
                {(row.columns || []).map((column, colIndex) => (
                  <div key={colIndex} style={{
                    minWidth: 0,
                    backgroundColor: column.column_bg_color || 'transparent',
                    height: '100%'
                  }}>
                    {column.type === 'category_list' && column.root_category_id && (() => {
                      const rootCat = categoryHierarchy.find(c => c.id === column.root_category_id);
                      if (!rootCat) return <div className="text-slate-400 text-sm">Category not found</div>;
                      
                      // Get custom items that should appear at top
                      const topItems = (column.custom_items || []).filter(item => item.position !== 'bottom');
                      const bottomItems = (column.custom_items || []).filter(item => item.position === 'bottom');
                      
                      return (
                        <div style={{
                          textAlign: currentMenu.mega_menu_center_content ? 'center' : 'left',
                          padding: `${currentMenu.category_list_padding !== undefined ? currentMenu.category_list_padding : 0}px`,
                          borderRadius: `${currentMenu.category_list_border_radius !== undefined ? currentMenu.category_list_border_radius : 0}px`
                        }}>
                          <h3 style={{
                            fontSize: '1.1em',
                            fontWeight: '600',
                            marginBottom: '8px',
                            color: currentMenu.mega_menu_root_color || currentMenu.text_color,
                            cursor: currentMenu.mega_menu_root_clickable !== false ? 'pointer' : 'default',
                            display: 'block',
                            padding: '8px 12px',
                            marginLeft: '-12px',
                            marginRight: '-12px',
                            borderRadius: '4px'
                          }}
                          className="mega-menu-root-category"
                          >
                            {rootCat.name}
                          </h3>
                          
                          {/* Divider as separate element */}
                          {currentMenu.mega_menu_category_divider !== false && (
                            <div style={{
                              height: `${currentMenu.mega_menu_category_divider_size || 2}px`,
                              backgroundColor: currentMenu.mega_menu_category_divider_color || currentMenu.primary_color,
                              marginBottom: '12px'
                            }}></div>
                          )}
                          
                          {/* Top positioned custom items */}
                          {topItems.length > 0 && (
                            <div style={{marginBottom: '12px'}}>
                              {topItems.map((item, idx) => {
                                const isButton = item.display_as === 'button';
                                const isLabel = item.display_as === 'label';
                                return (
                                  <div key={idx} style={{
                                    marginBottom: '8px',
                                    fontSize: isLabel ? '0.85em' : '0.95em',
                                    backgroundColor: isButton ? currentMenu.primary_color : 'transparent',
                                    color: isButton ? '#ffffff' : (isLabel ? (currentMenu.label_text_color || '#64748b') : currentMenu.dropdown_text_color),
                                    padding: isButton ? '8px 12px' : (isLabel ? '4px 12px' : '8px 12px'),
                                    borderRadius: isButton ? '6px' : '4px',
                                    fontWeight: isButton ? '600' : 'normal',
                                    fontStyle: (isLabel && item.italic) ? 'italic' : 'normal',
                                    textAlign: isButton ? 'center' : 'left',
                                    cursor: isLabel ? 'default' : 'pointer'
                                  }}
                                  className={isLabel ? '' : 'nav-preview-dropdown-item'}
                                  >
                                    {item.label}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Subcategories with labels */}
                          <div style={{listStyle: 'none', padding: 0, margin: 0}}>
                            {rootCat.children && rootCat.children.map((child) => {
                              const childLabels = (column.labels || []).filter(l => l.subcategory_id === child.id);
                              return (
                                <div key={child.id} className="mega-menu-subcategory" style={{
                                  marginBottom: '4px',
                                  fontSize: '0.95em',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  color: currentMenu.mega_menu_sub_color || currentMenu.dropdown_text_color,
                                  paddingLeft: `${(currentMenu.mega_menu_sub_padding || 0) + 12}px`,
                                  paddingRight: '12px',
                                  paddingTop: '8px',
                                  paddingBottom: '8px',
                                  marginLeft: '-12px',
                                  marginRight: '-12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}>
                                  <span>{child.name}</span>
                                  {childLabels.map((label, idx) => (
                                    <span key={idx} style={{
                                      fontSize: '0.7em',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      backgroundColor: label.use_default_color !== false ? (currentMenu.default_label_color || '#ef4444') : (label.color || '#ef4444'),
                                      color: '#ffffff',
                                      fontWeight: '600',
                                      textTransform: 'uppercase'
                                    }}>
                                      {label.text}
                                    </span>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Bottom positioned custom items */}
                          {bottomItems.length > 0 && (
                            <div style={{marginTop: '12px'}}>
                              {bottomItems.map((item, idx) => {
                                const isButton = item.display_as === 'button';
                                const isLabel = item.display_as === 'label';
                                return (
                                  <div key={idx} style={{
                                    marginBottom: '8px',
                                    fontSize: isLabel ? '0.85em' : '0.95em',
                                    backgroundColor: isButton ? currentMenu.primary_color : 'transparent',
                                    color: isButton ? '#ffffff' : (isLabel ? (currentMenu.label_text_color || '#64748b') : currentMenu.dropdown_text_color),
                                    padding: isButton ? '8px 12px' : (isLabel ? '4px 12px' : '8px 12px'),
                                    borderRadius: isButton ? '6px' : '4px',
                                    fontWeight: isButton ? '600' : 'normal',
                                    fontStyle: (isLabel && item.italic) ? 'italic' : 'normal',
                                    textAlign: isButton ? 'center' : 'left',
                                    cursor: isLabel ? 'default' : 'pointer'
                                  }}
                                  className={isLabel ? '' : 'nav-preview-dropdown-item'}
                                  >
                                    {item.label}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                    {column.type === 'card' && (
                      <div style={{
                        background: 'transparent',
                        borderRadius: `${currentMenu.card_border_radius !== undefined ? currentMenu.card_border_radius : 8}px`,
                        padding: `${currentMenu.card_padding !== undefined ? currentMenu.card_padding : 20}px`,
                        textAlign: 'center'
                      }}>
                        {column.image && (
                          <img 
                            src={column.image} 
                            alt={column.title || ''}
                            style={{
                              width: currentMenu.card_image_width || '100%',
                              height: currentMenu.card_image_height || 'auto',
                              borderRadius: '6px',
                              marginBottom: '12px',
                              objectFit: 'cover',
                              display: 'block',
                              marginLeft: 'auto',
                              marginRight: 'auto'
                            }}
                          />
                        )}
                        {column.title && (
                          <h4 style={{
                            fontSize: '1.1em',
                            fontWeight: '600',
                            marginBottom: '8px',
                            color: currentMenu.card_title_color || currentMenu.text_color
                          }}>
                            {column.title}
                          </h4>
                        )}
                        {column.description && (
                          <p style={{
                            fontSize: '0.9em',
                            color: currentMenu.card_description_color || currentMenu.dropdown_text_color,
                            marginBottom: '16px',
                            lineHeight: '1.5'
                          }}>
                            {column.description}
                          </p>
                        )}
                        {column.button_text && (
                          <CardButton 
                            text={column.button_text}
                            bgColor={currentMenu.card_button_bg_color || currentMenu.primary_color}
                            textColor={currentMenu.card_button_text_color || '#ffffff'}
                            hoverBgColor={currentMenu.card_button_hover_bg || currentMenu.card_button_bg_color || currentMenu.primary_color}
                            hoverTextColor={currentMenu.card_button_hover_text || currentMenu.card_button_text_color || '#ffffff'}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        
        {/* Expanding Categories Mega Menu Preview */}
        {currentMenu.mega_menu_enabled && currentMenu.mega_menu_content_type === 'expanding_categories' && (
          <div 
            className="mega-menu-preview expanding-preview" 
            style={{
              display: 'none',
              position: 'absolute',
              backgroundColor: currentMenu.mega_menu_dropdown_bg || currentMenu.dropdown_bg || '#ffffff',
              borderRadius: `${currentMenu.mega_menu_border_radius || 8}px`,
              boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
              zIndex: 9999,
              top: '100%',
              marginTop: '-62px',
              left: '0',
              overflow: 'visible'
            }}
            onMouseEnter={() => setIsMegaMenuOpen(true)}
            onMouseLeave={() => {
              setIsMegaMenuOpen(false);
              setPreviewExpandedColumns([]);
              const previewEl = document.querySelector('.mega-menu-preview.expanding-preview');
              if (previewEl) previewEl.style.display = 'none';
            }}
          >
            <div style={{
              display: 'flex',
              fontFamily: currentMenu.font_family,
              fontSize: `${currentMenu.font_size}px`,
              position: 'relative'
            }}>
              {/* Root categories column */}
              <div 
                data-column-index="0"
                style={{
                  width: currentMenu.column_width || '280px',
                  borderRight: '1px solid #e2e8f0',
                  backgroundColor: currentMenu.mega_menu_dropdown_bg || currentMenu.dropdown_bg || currentMenu.vertical_column_bg || '#ffffff',
                  position: 'relative',
                  zIndex: 10
                }}
              >
                {(currentMenu.included_categories || [])
                  .map((catId, index) => ({ catId, order: currentMenu.category_orders?.[catId] ?? index }))
                  .sort((a, b) => a.order - b.order)
                  .map(({ catId }) => {
                    const category = categoryHierarchy.find(c => c.id === catId);
                    if (!category) return null;
                    
                    // Check if category has children that are also selected
                    const hasVisibleChildren = category.children && category.children.length > 0 &&
                      category.children.some(child => (currentMenu.included_categories || []).includes(child.id));
                    
                    return (
                      <div
                        key={catId}
                        style={{
                          padding: '12px 16px',
                          cursor: hasVisibleChildren ? 'pointer' : 'default',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: currentMenu.dropdown_text_color || currentMenu.text_color || '#1f2937',
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background 0.2s'
                        }}
                        onClick={() => {
                          if (hasVisibleChildren) {
                            setPreviewExpandedColumns([{ category, depth: 0 }]);
                          } else if (category.url) {
                            window.open(category.url, '_blank');
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (hasVisibleChildren) {
                            e.currentTarget.style.backgroundColor = currentMenu.dropdown_hover_color || currentMenu.vertical_hover_bg || '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span>{category.name}</span>
                        {hasVisibleChildren && <span style={{fontSize: '1.2em'}}>›</span>}
                      </div>
                    );
                  })}
              </div>
              
              {/* Dynamically expanded columns */}
              {previewExpandedColumns.map((col, index) => {
                return (
                  <div
                    key={`expanded-${index}`}
                    data-column-index={index + 1}
                    style={{
                      width: currentMenu.column_width || '280px',
                      borderRight: '1px solid #e2e8f0',
                      backgroundColor: currentMenu.mega_menu_dropdown_bg || currentMenu.dropdown_bg || currentMenu.vertical_column_bg || '#ffffff',
                      position: 'relative',
                      zIndex: 10 - (index + 1),
                      opacity: 0,
                      transform: 'translateX(-100%)',
                      animation: 'slideInPreviewColumn 0.4s cubic-bezier(0.25, 1, 0.5, 1) forwards'
                    }}
                  >
                    {col.category.children && col.category.children
                      .filter(child => (currentMenu.included_categories || []).includes(child.id))
                      .map((child) => {
                        // Check if child has visible children (children that are also selected)
                        const hasVisibleChildren = child.children && child.children.length > 0 &&
                          child.children.some(grandchild => (currentMenu.included_categories || []).includes(grandchild.id));
                        
                        return (
                          <div
                            key={child.id}
                            style={{
                              padding: '12px 16px',
                              cursor: hasVisibleChildren ? 'pointer' : 'default',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              color: currentMenu.dropdown_text_color || currentMenu.text_color || '#1f2937',
                              borderBottom: '1px solid #f3f4f6',
                              transition: 'background 0.2s'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (hasVisibleChildren) {
                                // Add another column
                                setPreviewExpandedColumns(prev => [
                                  ...prev.slice(0, index + 1),
                                  { category: child, depth: col.depth + 1 }
                                ]);
                              } else if (child.url) {
                                window.open(child.url, '_blank');
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (hasVisibleChildren) {
                                e.currentTarget.style.backgroundColor = currentMenu.dropdown_hover_color || currentMenu.vertical_hover_bg || '#f3f4f6';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <span>{child.name}</span>
                            {hasVisibleChildren && <span style={{fontSize: '1.2em'}}>›</span>}
                          </div>
                        );
                      })}
                  </div>
                );
              })}
              
              {/* Card column - always at the end */}
              {(() => {
                // Get the appropriate card based on ROOT category (first in expansion path)
                // This ensures the card persists when drilling into subcategories
                const rootCategoryId = previewExpandedColumns.length > 0 
                  ? previewExpandedColumns[0].category.id 
                  : null;
                const cardData = getCardForColumn(rootCategoryId);
                if (!cardData) return null;
                
                const isCoverLayout = cardData.image_layout === 'cover';
                
                return (
                  <div 
                    key="card-column"
                    data-column-index={previewExpandedColumns.length + 1}
                    style={{
                      width: currentMenu.column_width || '280px',
                      borderRight: '1px solid #e2e8f0',
                      backgroundColor: cardData.bg_color || '#f9fafb',
                      position: 'relative',
                      zIndex: 9 - previewExpandedColumns.length,
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      ...(isCoverLayout && cardData.image_url ? {
                        backgroundImage: `url(${cardData.image_url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        minHeight: `${cardData.image_height || 300}px`,
                        justifyContent: 'flex-end'
                      } : {})
                    }}
                    onClick={() => cardData.button_url && window.open(cardData.button_url, '_blank')}
                  >
                    {isCoverLayout && cardData.image_url && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)',
                        pointerEvents: 'none'
                      }} />
                    )}
                    
                    <div style={{ position: 'relative', zIndex: 1, textAlign: cardData.content_alignment || 'center' }}>
                      {!isCoverLayout && cardData.image_url && (
                        <img 
                          src={cardData.image_url} 
                          alt={cardData.title || 'Card image'}
                          style={{
                            width: 'calc(100% + 32px)',
                            height: `${cardData.image_height || 120}px`,
                            objectFit: 'cover',
                            marginLeft: '-16px',
                            marginRight: '-16px',
                            marginTop: '-16px',
                            marginBottom: '12px'
                          }}
                        />
                      )}
                      {cardData.title && (
                        <div style={{
                          fontWeight: '600',
                          marginBottom: '8px',
                          color: isCoverLayout ? '#ffffff' : (cardData.title_color || currentMenu.text_color || '#1f2937')
                        }}>
                          {cardData.title}
                        </div>
                      )}
                      {cardData.description && (
                        <div style={{
                          fontSize: '0.875em',
                          marginBottom: '12px',
                          color: isCoverLayout ? '#f3f4f6' : (cardData.description_color || '#6b7280')
                        }}>
                          {cardData.description}
                        </div>
                      )}
                      {cardData.button_text && (
                        <button style={{
                          backgroundColor: cardData.button_bg_color || '#3b82f6',
                          color: cardData.button_text_color || '#ffffff',
                          padding: '10px 20px',
                          borderRadius: '4px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.875em',
                          fontWeight: '500',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          cardData.button_url && window.open(cardData.button_url, '_blank');
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = cardData.button_hover_bg_color || cardData.button_bg_color || '#2563eb';
                          e.currentTarget.style.color = cardData.button_hover_text_color || cardData.button_text_color || '#ffffff';
                          if (!cardData.button_hover_bg_color) {
                            e.currentTarget.style.opacity = '0.9';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = cardData.button_bg_color || '#3b82f6';
                          e.currentTarget.style.color = cardData.button_text_color || '#ffffff';
                          e.currentTarget.style.opacity = '1';
                        }}
                        >
                          {cardData.button_text}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
            
            {/* Add keyframes for column animation */}
            <style>{`
              @keyframes slideInPreviewColumn {
                from {
                  transform: translateX(-100%);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}</style>
          </div>
        )}
        
        {/* End of nav-preview-wrapper */}
        </div>
        </>
        )}
      </div>
    );
  };

  const renderCategorySelector = (items, depth = 0) => {
    if (!currentMenu) return null;
    
    return items.map((category) => {
      const isSelected = currentMenu.included_categories.includes(category.id);
      const hasChildren = category.children && category.children.length > 0;
      const autoIncludeSubcategories = currentMenu.category_auto_include_subcategories?.[category.id] !== false;
      
      return (
        <div key={category.id} style={{ marginLeft: `${depth * 20}px` }}>
          <div className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded">
            <label className="flex items-center gap-2 cursor-pointer flex-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleCategory(category.id)}
                className="w-4 h-4"
              />
              <span className="text-sm">{depth === 0 ? '📂' : '📁'} {category.name}</span>
            </label>
            
            {/* Auto-include subcategories toggle for root categories with children */}
            {depth === 0 && hasChildren && isSelected && (
              <label 
                className="flex items-center gap-1.5 text-xs cursor-pointer px-2 py-1 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={autoIncludeSubcategories}
                  onChange={(e) => {
                    setCurrentMenu(prev => ({
                      ...prev,
                      category_auto_include_subcategories: {
                        ...(prev.category_auto_include_subcategories || {}),
                        [category.id]: e.target.checked
                      }
                    }));
                  }}
                  className="w-3 h-3"
                />
                <span className="text-blue-700 font-medium whitespace-nowrap">Auto-include subs</span>
              </label>
            )}
          </div>
          {hasChildren && renderCategorySelector(category.children, depth + 1)}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading menus...</div>
      </div>
    );
  }

  if (!currentMenu) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <MenuIcon className="w-16 h-16 text-slate-300" />
        <div className="text-lg text-slate-600">No menus found</div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Your First Menu
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Menu Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold text-slate-800">Navigation Builder</h1>
          <select 
            value={selectedMenuId || ''} 
            onChange={(e) => setSelectedMenuId(e.target.value)}
            className="w-64 h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm hover:bg-slate-50 cursor-pointer"
          >
            {menus.map(menu => (
              <option key={menu.id} value={menu.id}>
                {menu.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Menu
          </Button>
          <Button variant="outline" onClick={() => handleDuplicateMenu(selectedMenuId)}>
            <Copy className="w-4 h-4 mr-2" />
            Duplicate
          </Button>
          <Button variant="outline" onClick={() => setShowCopyStyleDialog(true)}>
            <Palette className="w-4 h-4 mr-2" />
            Copy Style
          </Button>
          <Button 
            variant="outline" 
            onClick={() => {
              setDeleteMenuId(selectedMenuId);
              setShowDeleteDialog(true);
            }}
            disabled={menus.length === 1}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Live Preview at Top */}
      {currentMenu && renderLivePreview()}

      {/* Main Content */}
      <Tabs defaultValue="categories" className="w-full">
        {currentMenu.menu_type === 'footer' ? (
          // Footer Menu Tabs
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="categories" className="text-xs px-2">
              Categories
            </TabsTrigger>
            <TabsTrigger value="columns" className="text-xs px-2">
              Columns
            </TabsTrigger>
            <TabsTrigger value="cards" className="text-xs px-2">
              Cards
            </TabsTrigger>
            <TabsTrigger value="design" className="text-xs px-2">
              <Palette className="w-4 h-4 mr-1" />
              Design
            </TabsTrigger>
            <TabsTrigger value="mobile" className="text-xs px-2">
              <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                <line x1="12" y1="18" x2="12.01" y2="18"></line>
              </svg>
              Mobile
            </TabsTrigger>
            <TabsTrigger value="code" className="text-xs px-2">
              <Code className="w-4 h-4 mr-1" />
              Code
            </TabsTrigger>
          </TabsList>
        ) : (
          // Horizontal Menu Tabs
          <TabsList className={`grid w-full ${currentMenu.mega_menu_enabled && currentMenu.mega_menu_content_type === 'expanding_categories' ? 'grid-cols-7' : 'grid-cols-6'}`}>
            <TabsTrigger value="categories" className="text-xs px-2">
              Categories
            </TabsTrigger>
            <TabsTrigger value="custom" className="text-xs px-2">
              Placement
            </TabsTrigger>
            <TabsTrigger value="megamenu" className="text-xs px-2">
              Mega Menu
            </TabsTrigger>
            {currentMenu.mega_menu_enabled && currentMenu.mega_menu_content_type === 'expanding_categories' && (
              <TabsTrigger value="cards" className="text-xs px-2">
                Cards
              </TabsTrigger>
            )}
            <TabsTrigger value="design" className="text-xs px-2">
              <Palette className="w-4 h-4 mr-1" />
              Design
            </TabsTrigger>
            <TabsTrigger value="mobile" className="text-xs px-2">
              <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                <line x1="12" y1="18" x2="12.01" y2="18"></line>
              </svg>
              Mobile
            </TabsTrigger>
            <TabsTrigger value="code" className="text-xs px-2">
              <Code className="w-4 h-4 mr-1" />
              Code
            </TabsTrigger>
          </TabsList>
        )}

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Categories to Include</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAllCategories}>
                  Select All
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAllCategories}>
                  Deselect All
                </Button>
              </div>
            </div>
            
            {/* Auto-include new root categories toggle */}
            <div className="mb-4 p-3 bg-slate-50 rounded-md border border-slate-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentMenu.auto_include_new_root_categories || false}
                  onChange={(e) => setCurrentMenu(prev => ({
                    ...prev,
                    auto_include_new_root_categories: e.target.checked
                  }))}
                  className="w-4 h-4"
                />
                <div>
                  <span className="font-medium text-sm">Automatically include new root categories</span>
                  <p className="text-xs text-slate-600 mt-0.5">
                    When enabled, new root categories will be automatically added to this menu
                  </p>
                </div>
              </label>
            </div>
            
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {categoryHierarchy.length > 0 ? (
                renderCategorySelector(categoryHierarchy)
              ) : (
                <p className="text-slate-500 text-center py-8">No categories available</p>
              )}
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Menu'}
          </Button>

        </TabsContent>

        {/* Vertical Menu: Columns Tab */}
        {currentMenu.menu_type === 'vertical' && (
          <TabsContent value="columns" className="space-y-4">
            <div className="card">
              <h3 className="text-lg font-semibold mb-4">Column Configuration</h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Maximum Columns</Label>
                  <select
                    value={currentMenu.max_columns || 3}
                    onChange={(e) => setCurrentMenu(prev => ({...prev, max_columns: parseInt(e.target.value)}))}
                    className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                  >
                    <option value="1">1 Column (Single with back button)</option>
                    <option value="2">2 Columns</option>
                    <option value="3">3 Columns</option>
                    <option value="4">4 Columns</option>
                    <option value="5">5 Columns</option>
                  </select>
                  <p className="text-xs text-slate-500">
                    Maximum number of columns that can be displayed when drilling down into categories
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Column Width</Label>
                    <Input
                      type="text"
                      value={currentMenu.column_width || '280px'}
                      onChange={(e) => setCurrentMenu(prev => ({...prev, column_width: e.target.value}))}
                      placeholder="280px"
                    />
                    <p className="text-xs text-slate-500">Width of each column (px, %, rem)</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Column Gap (px)</Label>
                    <Input
                      type="number"
                      value={currentMenu.column_gap || 0}
                      onChange={(e) => setCurrentMenu(prev => ({...prev, column_gap: parseInt(e.target.value)}))}
                      min="0"
                      max="50"
                    />
                    <p className="text-xs text-slate-500">Space between columns</p>
                  </div>
                </div>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Menu'}
            </Button>
          </TabsContent>
        )}

        {/* Cards Tab - For Footer Menu and Expanding Categories */}
        {(currentMenu.menu_type === 'footer' || (currentMenu.mega_menu_enabled && currentMenu.mega_menu_content_type === 'expanding_categories')) && (
          <TabsContent value="cards" className="space-y-4">
            <div className="card">
              <h3 className="text-lg font-semibold mb-4">Category Cards</h3>
              <p className="text-sm text-slate-600 mb-4">
                Configure cards that appear in the rightmost column. You can set a default card and override it for specific categories.
              </p>

              {/* Default Card */}
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Default Card (Optional)</h4>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!currentMenu.default_vertical_card}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCurrentMenu(prev => ({
                            ...prev,
                            default_vertical_card: {
                              image: '',
                              title: 'Featured Products',
                              description: 'Check out our latest collection',
                              button_text: 'Shop Now',
                              button_url: '#'
                            }
                          }));
                        } else {
                          setCurrentMenu(prev => ({...prev, default_vertical_card: null}));
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Enable default card</span>
                  </label>
                </div>

                {currentMenu.default_vertical_card && (
                  <div className="space-y-3 mt-3">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Card Image</label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setMediaBrowserCallback(() => (url) => {
                            setCurrentMenu(prev => ({
                              ...prev,
                              default_vertical_card: {...prev.default_vertical_card, image_url: url}
                            }));
                          });
                          setShowMediaBrowser(true);
                        }}
                      >
                        📁 Choose from Media
                      </Button>
                      {currentMenu.default_vertical_card.image_url && (
                        <div className="mt-2">
                          <img 
                            src={currentMenu.default_vertical_card.image_url} 
                            alt="Card preview" 
                            className="h-24 w-auto object-cover rounded border"
                          />
                          <button
                            onClick={() => setCurrentMenu(prev => ({
                              ...prev,
                              default_vertical_card: {...prev.default_vertical_card, image_url: ''}
                            }))}
                            className="text-red-600 text-sm mt-1 hover:underline"
                          >
                            Remove Image
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Image Layout Option */}
                    <div className="space-y-2">
                      <Label className="text-sm">Image Layout</Label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="image_layout"
                            value="standard"
                            checked={!currentMenu.default_vertical_card.image_layout || currentMenu.default_vertical_card.image_layout === 'standard'}
                            onChange={(e) => setCurrentMenu(prev => ({
                              ...prev,
                              default_vertical_card: {...prev.default_vertical_card, image_layout: e.target.value}
                            }))}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Standard (Image top, text below)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="image_layout"
                            value="cover"
                            checked={currentMenu.default_vertical_card.image_layout === 'cover'}
                            onChange={(e) => setCurrentMenu(prev => ({
                              ...prev,
                              default_vertical_card: {...prev.default_vertical_card, image_layout: e.target.value}
                            }))}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Cover (Text overlay on image)</span>
                        </label>
                      </div>
                    </div>
                    
                    <Input
                      placeholder="Title"
                      value={currentMenu.default_vertical_card.title || ''}
                      onChange={(e) => setCurrentMenu(prev => ({
                        ...prev,
                        default_vertical_card: {...prev.default_vertical_card, title: e.target.value}
                      }))}
                    />
                    <Textarea
                      placeholder="Description"
                      value={currentMenu.default_vertical_card.description || ''}
                      onChange={(e) => setCurrentMenu(prev => ({
                        ...prev,
                        default_vertical_card: {...prev.default_vertical_card, description: e.target.value}
                      }))}
                      rows={3}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Button Text"
                        value={currentMenu.default_vertical_card.button_text || ''}
                        onChange={(e) => setCurrentMenu(prev => ({
                          ...prev,
                          default_vertical_card: {...prev.default_vertical_card, button_text: e.target.value}
                        }))}
                      />
                      <Input
                        placeholder="Button URL"
                        value={currentMenu.default_vertical_card.button_url || ''}
                        onChange={(e) => setCurrentMenu(prev => ({
                          ...prev,
                          default_vertical_card: {...prev.default_vertical_card, button_url: e.target.value}
                        }))}
                      />
                    </div>
                    
                    {/* Card Styling Options */}
                    <div className="pt-4 mt-4 border-t border-slate-200">
                      <h4 className="font-medium mb-3 text-sm">Card Styling</h4>
                      
                      <div className="space-y-3">
                        {/* Colors */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Card Background</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={currentMenu.default_vertical_card.bg_color || '#f9fafb'}
                                onChange={(e) => setCurrentMenu(prev => ({
                                  ...prev,
                                  default_vertical_card: {...prev.default_vertical_card, bg_color: e.target.value}
                                }))}
                                className="w-16"
                              />
                              <Input
                                value={currentMenu.default_vertical_card.bg_color || '#f9fafb'}
                                onChange={(e) => setCurrentMenu(prev => ({
                                  ...prev,
                                  default_vertical_card: {...prev.default_vertical_card, bg_color: e.target.value}
                                }))}
                                placeholder="#f9fafb"
                                className="flex-1"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs">Title Color</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={currentMenu.default_vertical_card.title_color || '#1f2937'}
                                onChange={(e) => setCurrentMenu(prev => ({
                                  ...prev,
                                  default_vertical_card: {...prev.default_vertical_card, title_color: e.target.value}
                                }))}
                                className="w-16"
                              />
                              <Input
                                value={currentMenu.default_vertical_card.title_color || '#1f2937'}
                                onChange={(e) => setCurrentMenu(prev => ({
                                  ...prev,
                                  default_vertical_card: {...prev.default_vertical_card, title_color: e.target.value}
                                }))}
                                placeholder="#1f2937"
                                className="flex-1"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Description Color</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={currentMenu.default_vertical_card.description_color || '#6b7280'}
                                onChange={(e) => setCurrentMenu(prev => ({
                                  ...prev,
                                  default_vertical_card: {...prev.default_vertical_card, description_color: e.target.value}
                                }))}
                                className="w-16"
                              />
                              <Input
                                value={currentMenu.default_vertical_card.description_color || '#6b7280'}
                                onChange={(e) => setCurrentMenu(prev => ({
                                  ...prev,
                                  default_vertical_card: {...prev.default_vertical_card, description_color: e.target.value}
                                }))}
                                placeholder="#6b7280"
                                className="flex-1"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs">Image Height (px)</Label>
                            <Input
                              type="number"
                              value={currentMenu.default_vertical_card.image_height || 120}
                              onChange={(e) => setCurrentMenu(prev => ({
                                ...prev,
                                default_vertical_card: {...prev.default_vertical_card, image_height: parseInt(e.target.value)}
                              }))}
                              min="50"
                              max="400"
                            />
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs">Content Alignment</Label>
                            <select
                              value={currentMenu.default_vertical_card.content_alignment || 'center'}
                              onChange={(e) => setCurrentMenu(prev => ({
                                ...prev,
                                default_vertical_card: {...prev.default_vertical_card, content_alignment: e.target.value}
                              }))}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </select>
                          </div>
                        </div>
                        
                        {/* Button Colors */}
                        <div className="pt-2">
                          <Label className="text-xs font-medium mb-2 block">Button Styling</Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Button Background</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={currentMenu.default_vertical_card.button_bg_color || '#3b82f6'}
                                  onChange={(e) => setCurrentMenu(prev => ({
                                    ...prev,
                                    default_vertical_card: {...prev.default_vertical_card, button_bg_color: e.target.value}
                                  }))}
                                  className="w-16"
                                />
                                <Input
                                  value={currentMenu.default_vertical_card.button_bg_color || '#3b82f6'}
                                  onChange={(e) => setCurrentMenu(prev => ({
                                    ...prev,
                                    default_vertical_card: {...prev.default_vertical_card, button_bg_color: e.target.value}
                                  }))}
                                  placeholder="#3b82f6"
                                  className="flex-1"
                                />
                              </div>
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-xs">Button Text Color</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={currentMenu.default_vertical_card.button_text_color || '#ffffff'}
                                  onChange={(e) => setCurrentMenu(prev => ({
                                    ...prev,
                                    default_vertical_card: {...prev.default_vertical_card, button_text_color: e.target.value}
                                  }))}
                                  className="w-16"
                                />
                                <Input
                                  value={currentMenu.default_vertical_card.button_text_color || '#ffffff'}
                                  onChange={(e) => setCurrentMenu(prev => ({
                                    ...prev,
                                    default_vertical_card: {...prev.default_vertical_card, button_text_color: e.target.value}
                                  }))}
                                  placeholder="#ffffff"
                                  className="flex-1"
                                />
                              </div>
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-xs">Button Hover Background</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={currentMenu.default_vertical_card.button_hover_bg_color || currentMenu.default_vertical_card.button_bg_color || '#2563eb'}
                                  onChange={(e) => setCurrentMenu(prev => ({
                                    ...prev,
                                    default_vertical_card: {...prev.default_vertical_card, button_hover_bg_color: e.target.value}
                                  }))}
                                  className="w-16"
                                />
                                <Input
                                  value={currentMenu.default_vertical_card.button_hover_bg_color || currentMenu.default_vertical_card.button_bg_color || '#2563eb'}
                                  onChange={(e) => setCurrentMenu(prev => ({
                                    ...prev,
                                    default_vertical_card: {...prev.default_vertical_card, button_hover_bg_color: e.target.value}
                                  }))}
                                  placeholder="#2563eb"
                                  className="flex-1"
                                />
                              </div>
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-xs">Button Hover Text Color</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="color"
                                  value={currentMenu.default_vertical_card.button_hover_text_color || currentMenu.default_vertical_card.button_text_color || '#ffffff'}
                                  onChange={(e) => setCurrentMenu(prev => ({
                                    ...prev,
                                    default_vertical_card: {...prev.default_vertical_card, button_hover_text_color: e.target.value}
                                  }))}
                                  className="w-16"
                                />
                                <Input
                                  value={currentMenu.default_vertical_card.button_hover_text_color || currentMenu.default_vertical_card.button_text_color || '#ffffff'}
                                  onChange={(e) => setCurrentMenu(prev => ({
                                    ...prev,
                                    default_vertical_card: {...prev.default_vertical_card, button_hover_text_color: e.target.value}
                                  }))}
                                  placeholder="#ffffff"
                                  className="flex-1"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Category-Specific Cards */}
              <div className="mt-6">
                <h4 className="font-medium mb-3">Category-Specific Cards</h4>
                <p className="text-xs text-slate-500 mb-4">
                  Override the default card for specific root categories
                </p>
                
                {/* List existing category cards */}
                {currentMenu.column_specific_cards && Object.keys(currentMenu.column_specific_cards).length > 0 && (
                  <div className="space-y-2 mb-4">
                    {Object.entries(currentMenu.column_specific_cards).map(([categoryId, cardData]) => {
                      const category = categoryHierarchy.find(c => c.id === categoryId);
                      return (
                        <div key={categoryId} className="flex items-center justify-between p-3 bg-slate-50 rounded border">
                          <div>
                            <span className="font-medium text-sm">{category?.name || 'Unknown Category'}</span>
                            <p className="text-xs text-slate-500 mt-1">
                              {cardData.title || 'No title'} - {cardData.button_text || 'No button'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingCategoryCardId(categoryId);
                                setShowCategoryCardDialog(true);
                              }}
                              className="text-blue-600 hover:text-blue-700 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                const newCards = {...currentMenu.column_specific_cards};
                                delete newCards[categoryId];
                                setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                              }}
                              className="text-red-600 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setEditingCategoryCardId(null);
                    setShowCategoryCardDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category Card
                </Button>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Menu'}
            </Button>
          </TabsContent>
        )}

        {/* Menu Placement Tab (Horizontal only) */}
        {currentMenu.menu_type !== 'vertical' && (
        <TabsContent value="custom" className="space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Menu Placement & Order</h3>
                <p className="text-sm text-slate-600 mt-1">Organize categories and custom items by position (left, center, right)</p>
              </div>
              <Button onClick={() => setShowCustomItemDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Custom Item
              </Button>
            </div>

            {(() => {
              const allItems = getAllMenuItems();
              const leftItems = allItems.filter(item => item.position === 'left').sort((a, b) => a.order - b.order);
              const centerItems = allItems.filter(item => item.position === 'center').sort((a, b) => a.order - b.order);
              const rightItems = allItems.filter(item => item.position === 'right').sort((a, b) => a.order - b.order);

              return (
                <div className="grid grid-cols-3 gap-4">
                  {/* Left Position */}
                  <div className="border-2 border-slate-200 rounded-lg p-4">
                    <h4 className="font-semibold text-sm mb-3 text-slate-700 flex items-center gap-2">
                      <span className="text-lg">←</span> Left ({leftItems.length})
                    </h4>
                    <div className="space-y-2">
                      {leftItems.length > 0 ? leftItems.map((item, idx) => (
                        <div key={`${item.type}-${item.id}`} className="bg-slate-50 rounded p-2 border border-slate-200">
                          <div className="flex items-center gap-2">
                            {/* Move up/down */}
                            <div className="flex flex-col">
                              <button
                                onClick={() => moveMenuItem(item, 'up', leftItems)}
                                disabled={idx === 0}
                                className="p-0.5 hover:bg-slate-200 rounded disabled:opacity-30"
                                title="Move up"
                              >
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M8 4l-4 4h8l-4-4z"/>
                                </svg>
                              </button>
                              <button
                                onClick={() => moveMenuItem(item, 'down', leftItems)}
                                disabled={idx === leftItems.length - 1}
                                className="p-0.5 hover:bg-slate-200 rounded disabled:opacity-30"
                                title="Move down"
                              >
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M8 12l4-4H4l4 4z"/>
                                </svg>
                              </button>
                            </div>
                            
                            <div className="flex-1">
                              <div className="text-sm font-medium">{item.name}</div>
                              <div className="text-xs text-slate-500">
                                {item.type === 'category' ? '📂 Category' : (item.type === 'megamenu' ? '⚡ Mega Menu' : '🔗 Custom')}
                              </div>
                            </div>
                            
                            {/* Edit and Delete buttons for custom items */}
                            {item.type === 'custom' && item.type !== 'megamenu' && (
                              <div className="flex gap-1 mr-1">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEditCustomItem(item.customIndex);
                                  }}
                                  className="p-1 hover:bg-green-100 rounded text-xs cursor-pointer text-green-600"
                                  title="Edit"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeleteCustomItem(item.customIndex);
                                  }}
                                  className="p-1 hover:bg-red-100 rounded text-xs cursor-pointer text-red-600"
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </div>
                            )}
                            
                            {/* Position changer */}
                            <div className="flex gap-0.5">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('C button clicked for item:', item.type, item.name);
                                  if (item.type === 'category') {
                                    updateCategoryPosition(item.id, 'center');
                                  } else if (item.type === 'custom') {
                                    updateCustomItemPosition(item.customIndex, 'center');
                                  } else if (item.type === 'megamenu') {
                                    updateMegaMenuPosition('center');
                                  }
                                }}
                                className="p-1 hover:bg-blue-100 rounded text-xs cursor-pointer"
                                title="Move to center"
                              >C</button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('R button clicked for item:', item.type, item.name);
                                  if (item.type === 'category') {
                                    updateCategoryPosition(item.id, 'right');
                                  } else if (item.type === 'custom') {
                                    updateCustomItemPosition(item.customIndex, 'right');
                                  } else if (item.type === 'megamenu') {
                                    updateMegaMenuPosition('right');
                                  }
                                }}
                                className="p-1 hover:bg-amber-100 rounded text-xs cursor-pointer"
                                title="Move to right"
                              >R</button>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <p className="text-xs text-slate-400 text-center py-4">No items on left</p>
                      )}
                    </div>
                  </div>

                  {/* Center Position */}
                  <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                    <h4 className="font-semibold text-sm mb-3 text-blue-700 flex items-center gap-2">
                      <span className="text-lg">↕</span> Center ({centerItems.length})
                    </h4>
                    <div className="space-y-2">
                      {centerItems.length > 0 ? centerItems.map((item, idx) => (
                        <div key={`${item.type}-${item.id}`} className="bg-white rounded p-2 border border-blue-200">
                          <div className="flex items-center gap-2">
                            {/* Move up/down */}
                            <div className="flex flex-col">
                              <button
                                onClick={() => moveMenuItem(item, 'up', centerItems)}
                                disabled={idx === 0}
                                className="p-0.5 hover:bg-blue-100 rounded disabled:opacity-30"
                              >
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M8 4l-4 4h8l-4-4z"/>
                                </svg>
                              </button>
                              <button
                                onClick={() => moveMenuItem(item, 'down', centerItems)}
                                disabled={idx === centerItems.length - 1}
                                className="p-0.5 hover:bg-blue-100 rounded disabled:opacity-30"
                              >
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M8 12l4-4H4l4 4z"/>
                                </svg>
                              </button>
                            </div>
                            
                            <div className="flex-1">
                              <div className="text-sm font-medium">{item.name}</div>
                              <div className="text-xs text-slate-500">
                                {item.type === 'category' ? '📂 Category' : (item.type === 'megamenu' ? '⚡ Mega Menu' : '🔗 Custom')}
                              </div>
                            </div>
                            
                            {/* Edit and Delete buttons for custom items */}
                            {item.type === 'custom' && item.type !== 'megamenu' && (
                              <div className="flex gap-1 mr-1">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEditCustomItem(item.customIndex);
                                  }}
                                  className="p-1 hover:bg-green-100 rounded text-xs cursor-pointer text-green-600"
                                  title="Edit"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeleteCustomItem(item.customIndex);
                                  }}
                                  className="p-1 hover:bg-red-100 rounded text-xs cursor-pointer text-red-600"
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </div>
                            )}
                            
                            <div className="flex gap-0.5">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('L button clicked for item:', item.type, item.name);
                                  if (item.type === 'category') {
                                    updateCategoryPosition(item.id, 'left');
                                  } else if (item.type === 'custom') {
                                    updateCustomItemPosition(item.customIndex, 'left');
                                  } else if (item.type === 'megamenu') {
                                    updateMegaMenuPosition('left');
                                  }
                                }}
                                className="p-1 hover:bg-slate-100 rounded text-xs cursor-pointer"
                              >L</button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('R button clicked for item:', item.type, item.name);
                                  if (item.type === 'category') {
                                    updateCategoryPosition(item.id, 'right');
                                  } else if (item.type === 'custom') {
                                    updateCustomItemPosition(item.customIndex, 'right');
                                  } else if (item.type === 'megamenu') {
                                    updateMegaMenuPosition('right');
                                  }
                                }}
                                className="p-1 hover:bg-amber-100 rounded text-xs cursor-pointer"
                              >R</button>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <p className="text-xs text-slate-400 text-center py-4">No items in center</p>
                      )}
                    </div>
                  </div>

                  {/* Right Position */}
                  <div className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50">
                    <h4 className="font-semibold text-sm mb-3 text-amber-700 flex items-center gap-2">
                      <span className="text-lg">→</span> Right ({rightItems.length})
                    </h4>
                    <div className="space-y-2">
                      {rightItems.length > 0 ? rightItems.map((item, idx) => (
                        <div key={`${item.type}-${item.id}`} className="bg-white rounded p-2 border border-amber-200">
                          <div className="flex items-center gap-2">
                            {/* Move up/down */}
                            <div className="flex flex-col">
                              <button
                                onClick={() => moveMenuItem(item, 'up', rightItems)}
                                disabled={idx === 0}
                                className="p-0.5 hover:bg-amber-100 rounded disabled:opacity-30"
                              >
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M8 4l-4 4h8l-4-4z"/>
                                </svg>
                              </button>
                              <button
                                onClick={() => moveMenuItem(item, 'down', rightItems)}
                                disabled={idx === rightItems.length - 1}
                                className="p-0.5 hover:bg-amber-100 rounded disabled:opacity-30"
                              >
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M8 12l4-4H4l4 4z"/>
                                </svg>
                              </button>
                            </div>
                            
                            <div className="flex-1">
                              <div className="text-sm font-medium">{item.name}</div>
                              <div className="text-xs text-slate-500">
                                {item.type === 'category' ? '📂 Category' : (item.type === 'megamenu' ? '⚡ Mega Menu' : '🔗 Custom')}
                              </div>
                            </div>
                            
                            {/* Edit and Delete buttons for custom items */}
                            {item.type === 'custom' && item.type !== 'megamenu' && (
                              <div className="flex gap-1 mr-1">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEditCustomItem(item.customIndex);
                                  }}
                                  className="p-1 hover:bg-green-100 rounded text-xs cursor-pointer text-green-600"
                                  title="Edit"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeleteCustomItem(item.customIndex);
                                  }}
                                  className="p-1 hover:bg-red-100 rounded text-xs cursor-pointer text-red-600"
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </div>
                            )}
                            
                            <div className="flex gap-0.5">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('L button clicked for item:', item.type, item.name);
                                  if (item.type === 'category') {
                                    updateCategoryPosition(item.id, 'left');
                                  } else if (item.type === 'custom') {
                                    updateCustomItemPosition(item.customIndex, 'left');
                                  } else if (item.type === 'megamenu') {
                                    updateMegaMenuPosition('left');
                                  }
                                }}
                                className="p-1 hover:bg-slate-100 rounded text-xs cursor-pointer"
                              >L</button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('C button clicked for item:', item.type, item.name);
                                  if (item.type === 'category') {
                                    updateCategoryPosition(item.id, 'center');
                                  } else if (item.type === 'custom') {
                                    updateCustomItemPosition(item.customIndex, 'center');
                                  } else if (item.type === 'megamenu') {
                                    updateMegaMenuPosition('center');
                                  }
                                }}
                                className="p-1 hover:bg-blue-100 rounded text-xs cursor-pointer"
                              >C</button>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <p className="text-xs text-slate-400 text-center py-4">No items on right</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>💡 Tip:</strong> Use L/C/R buttons to move items between positions. Use ↑↓ arrows to reorder within each position.
              </p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Menu'}
          </Button>

        </TabsContent>
        )}

        {/* Mega Menu Tab (Horizontal only) */}
        {currentMenu.menu_type !== 'vertical' && (
        <TabsContent value="megamenu" className="space-y-4">
          <div className="card space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-1">Mega Menu Configuration</h3>
              <p className="text-sm text-slate-500">Create a flexible mega menu with custom rows and columns</p>
            </div>

            {/* Enable Mega Menu */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={currentMenu.mega_menu_enabled || false}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setCurrentMenu({
                    ...currentMenu, 
                    mega_menu_enabled: enabled,
                    // Initialize mega_menu_button with position and order if enabling
                    mega_menu_button: enabled ? {
                      label: currentMenu.mega_menu_button?.label || 'Shop',
                      position: currentMenu.mega_menu_button?.position || 'left',
                      order: currentMenu.mega_menu_button?.order ?? 0
                    } : currentMenu.mega_menu_button
                  });
                }}
                className="w-5 h-5"
              />
              <div>
                <div className="font-medium">Enable Mega Menu</div>
                <div className="text-sm text-slate-500">Show a dropdown menu button with customizable layout</div>
              </div>
            </label>
            
            {/* Save Button at top for convenience */}
            {currentMenu.mega_menu_enabled && (
              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Menu'}
                </Button>
              </div>
            )}

            {currentMenu.mega_menu_enabled && (
              <>
                {/* Content Type Selector */}
                <div className="space-y-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="text-base font-semibold">Mega Menu Content Type</Label>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer p-3 border-2 rounded-lg hover:bg-white transition-colors"
                      style={{borderColor: currentMenu.mega_menu_content_type === 'category_grid' ? '#4f46e5' : '#e2e8f0'}}>
                      <input
                        type="radio"
                        name="megaMenuContentType"
                        value="category_grid"
                        checked={(currentMenu.mega_menu_content_type || 'category_grid') === 'category_grid'}
                        onChange={(e) => setCurrentMenu(prev => ({...prev, mega_menu_content_type: e.target.value}))}
                        className="w-5 h-5 mt-0.5"
                      />
                      <div>
                        <div className="font-medium">Category Grid</div>
                        <div className="text-sm text-slate-600">Traditional mega menu with rows and columns of categories</div>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer p-3 border-2 rounded-lg hover:bg-white transition-colors"
                      style={{borderColor: currentMenu.mega_menu_content_type === 'expanding_categories' ? '#4f46e5' : '#e2e8f0'}}>
                      <input
                        type="radio"
                        name="megaMenuContentType"
                        value="expanding_categories"
                        checked={currentMenu.mega_menu_content_type === 'expanding_categories'}
                        onChange={(e) => setCurrentMenu(prev => ({...prev, mega_menu_content_type: e.target.value}))}
                        className="w-5 h-5 mt-0.5"
                      />
                      <div>
                        <div className="font-medium">Expanding Categories</div>
                        <div className="text-sm text-slate-600">
                          Column-based navigation that expands as users drill down.<br/>
                          <span className="text-blue-600 font-medium">Choose your categories from the Categories tab.</span>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Display Style Selector - Only show for Expanding Categories */}
                {currentMenu.mega_menu_content_type === 'expanding_categories' && (
                  <div className="space-y-2">
                    <Label>Display Style</Label>
                    <select
                      value={currentMenu.mega_menu_display_style || 'dropdown'}
                      onChange={(e) => setCurrentMenu(prev => ({...prev, mega_menu_display_style: e.target.value}))}
                      className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                    >
                      <option value="dropdown">Dropdown Below Button</option>
                      <option value="centered_overlay">Centered Overlay</option>
                      <option value="slide_left">Slide In from Left</option>
                      <option value="slide_right">Slide In from Right</option>
                    </select>
                    <p className="text-xs text-slate-500">How the expanding menu appears when opened</p>
                  </div>
                )}

                {/* Mega Menu Button Config */}
                <div className="space-y-1">
                  <Label>Button Label</Label>
                  <Input
                    type="text"
                    value={(currentMenu.mega_menu_button || {}).label || 'Shop'}
                    onChange={(e) => setCurrentMenu({
                      ...currentMenu, 
                      mega_menu_button: {...(currentMenu.mega_menu_button || {}), label: e.target.value}
                    })}
                    placeholder="Button label (e.g., Shop)"
                  />
                </div>

                {/* Expanding Categories Settings */}
                {currentMenu.mega_menu_content_type === 'expanding_categories' && (
                  <div className="space-y-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <h4 className="font-semibold">Expanding Categories Configuration</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Maximum Columns</Label>
                        <select
                          value={currentMenu.max_columns || 3}
                          onChange={(e) => setCurrentMenu(prev => ({...prev, max_columns: parseInt(e.target.value)}))}
                          className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                        >
                          <option value="1">1 Column (Single with back button)</option>
                          <option value="2">2 Columns</option>
                          <option value="3">3 Columns</option>
                          <option value="4">4 Columns</option>
                          <option value="5">5 Columns</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label>Column Width</Label>
                        <Input
                          type="text"
                          value={currentMenu.column_width || '280px'}
                          onChange={(e) => setCurrentMenu(prev => ({...prev, column_width: e.target.value}))}
                          placeholder="280px"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Visual Row/Column Configurator - Only show for Category Grid */}
                {currentMenu.mega_menu_content_type !== 'expanding_categories' && (
                <div className="space-y-3 pt-3 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Mega Menu Rows</Label>
                    <Button 
                      size="sm" 
                      onClick={() => {
                        setNewRowColumns(3);
                        setShowAddRowDialog(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Row
                    </Button>
                  </div>

                  {/* Visual Rows Preview */}
                  {(currentMenu.mega_menu_rows || []).length > 0 ? (
                    <div className="space-y-4">
                      {(currentMenu.mega_menu_rows || []).map((row, rowIndex) => (
                        <div key={rowIndex} className="border-2 border-slate-300 rounded-lg p-4 bg-slate-50">
                          <div className="flex items-center justify-between mb-3">
                            <div className="font-medium text-slate-700">Row {rowIndex + 1} ({(row.columns || []).length} columns)</div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  const rows = [...(currentMenu.mega_menu_rows || [])];
                                  rows[rowIndex].columns.push({type: 'category_list', root_category_id: '', auto_include_subcategories: true});
                                  setCurrentMenu({...currentMenu, mega_menu_rows: rows});
                                }}
                                className="text-green-600 hover:bg-green-50 p-1 rounded"
                                title="Add Column"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setRemoveColumnData({ rowIndex, columnCount: (row.columns || []).length });
                                  setShowRemoveColumnDialog(true);
                                }}
                                className="text-orange-600 hover:bg-orange-50 p-1 rounded disabled:opacity-50"
                                disabled={(row.columns || []).length <= 1}
                                title="Remove Column"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteRowIndex(rowIndex);
                                  setShowDeleteRowDialog(true);
                                }}
                                className="text-red-600 hover:bg-red-50 p-1 rounded"
                                title="Delete Row"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${(row.columns || []).length}, 1fr)`,
                            gap: '12px'
                          }}>
                            {(row.columns || []).map((column, colIndex) => {
                              const rootCat = column.root_category_id ? categoryHierarchy.find(c => c.id === column.root_category_id) : null;
                              return (
                                <div
                                  key={colIndex}
                                  className="border-2 border-slate-300 rounded-lg p-3 bg-white hover:border-indigo-500 hover:shadow-md transition-all group relative min-h-[150px] flex flex-col"
                                >
                                  {/* Hover Overlay with Actions */}
                                  <div className="absolute inset-0 bg-indigo-600/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg pointer-events-none" />
                                  
                                  {/* Edit Indicator - Center */}
                                  <div 
                                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer pointer-events-auto z-10"
                                    onClick={() => {
                                      setEditingColumn(column);
                                      setEditingRowIndex(rowIndex);
                                      setEditingColIndex(colIndex);
                                      setShowColumnEditor(true);
                                    }}
                                  >
                                    <div className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg font-medium flex items-center gap-2">
                                      <Edit2 className="w-4 h-4" />
                                      Click to Edit
                                    </div>
                                  </div>

                                  {/* Action Buttons - Top Right */}
                                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto z-10">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDuplicateColumn(rowIndex, colIndex);
                                      }}
                                      className="bg-blue-600 text-white p-1.5 rounded hover:bg-blue-700 transition-colors"
                                      title="Duplicate Column"
                                    >
                                      <Files className="w-4 h-4" />
                                    </button>
                                  </div>

                                  {/* Move Arrows - Sides */}
                                  {colIndex > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        moveColumn(rowIndex, colIndex, 'left');
                                      }}
                                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-slate-700 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-800 pointer-events-auto z-10"
                                      title="Move Left"
                                    >
                                      <ArrowLeft className="w-4 h-4" />
                                    </button>
                                  )}
                                  {colIndex < (row.columns || []).length - 1 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        moveColumn(rowIndex, colIndex, 'right');
                                      }}
                                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-slate-700 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-800 pointer-events-auto z-10"
                                      title="Move Right"
                                    >
                                      <ArrowRight className="w-4 h-4" />
                                    </button>
                                  )}

                                  {/* Column Content */}
                                  <div className="text-xs text-slate-500 mb-2 flex items-center justify-between">
                                    <span>{column.type === 'card' ? 'Custom Card' : 'Category List'}</span>
                                    {column.type === 'category_list' && column.auto_include_subcategories !== false && rootCat && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title="Auto-includes new subcategories">
                                        Auto ✓
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex-1 flex flex-col">
                                    {column.type === 'category_list' && rootCat ? (
                                      <>
                                        <div className="font-semibold text-sm mb-1">{rootCat.name}</div>
                                        {rootCat.children && rootCat.children.length > 0 && (
                                          <div className="text-xs text-slate-600 space-y-0.5">
                                            {rootCat.children.slice(0, 3).map(child => (
                                              <div key={child.id}>• {child.name}</div>
                                            ))}
                                            {rootCat.children.length > 3 && (
                                              <div className="text-slate-400">+{rootCat.children.length - 3} more</div>
                                            )}
                                          </div>
                                        )}
                                      </>
                                    ) : column.type === 'card' ? (
                                      <>
                                        {column.image && (
                                          <img src={column.image} alt="" className="w-full h-16 object-cover rounded mb-1" />
                                        )}
                                        {column.title && (
                                          <div className="font-semibold text-xs mb-0.5">{column.title}</div>
                                        )}
                                        {column.description && (
                                          <div className="text-xs text-slate-600 mb-1">{column.description.slice(0, 30)}...</div>
                                        )}
                                        {column.button_text && (
                                          <div className="mt-auto bg-indigo-600 text-white text-xs px-2 py-0.5 rounded text-center">
                                            {column.button_text}
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="flex items-center justify-center h-full text-slate-400 text-xs">
                                        Empty<br/>Click to edit
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center bg-slate-50">
                      <div className="text-slate-400 mb-2">No rows added yet</div>
                      <div className="text-sm text-slate-500">Click "Add Row" to start building your mega menu</div>
                    </div>
                  )}
                </div>
                )}

                {/* Styling Options Below */}
                <div className="space-y-3 pt-4 border-t-2 border-slate-200">
                  <h4 className="font-semibold text-slate-700">Styling Options</h4>
                </div>

                {/* Arrow Toggle & Customization */}
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentMenu.mega_menu_show_arrow !== false}
                      onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_show_arrow: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <div>
                      <div className="font-medium">Show Dropdown Arrow on Mega Menu Button</div>
                      <div className="text-sm text-slate-500">Display arrow next to mega menu button only</div>
                    </div>
                  </label>

                  {(currentMenu.mega_menu_show_arrow !== false) && (
                    <>
                      <Label>Arrow Style</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { value: 'chevron', label: 'Chevron', icon: (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="24" height="24">
                              <path fill="currentColor" d="M38,8.064v10.452l-18,14.21L2,18.515V8.064l17.38,13.721L20,22.274l0.62-0.489L38,8.064 M39,6L20,21L1,6v13l19,15l19-15V6L39,6z"/>
                            </svg>
                          )},
                          { value: 'circled', label: 'Circled', icon: (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="28" height="28">
                              <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" strokeWidth="2"/>
                              <path fill="currentColor" d="M9 17L12 14 20 22 28 14 31 17 20 28z"/>
                            </svg>
                          )},
                          { value: 'double', label: 'Double', icon: (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="24" height="24">
                              <path fill="currentColor" d="M36.433,7.395l1.168,1.602L20,21.762L2.399,8.997l1.168-1.602l15.846,11.492L20,19.313l0.587-0.426L36.433,7.395 M36.653,6L20,18.078L3.347,6L1,9.218l19,13.779L39,9.218L36.653,6L36.653,6z"/>
                              <path fill="currentColor" d="M36.433,18.391l1.169,1.603L20,32.765L2.399,19.994l1.169-1.603l15.845,11.497L20,30.314l0.587-0.426L36.433,18.391 M36.653,16.996L20,29.079L3.347,16.996L1,20.215L20,34l19-13.785L36.653,16.996L36.653,16.996z"/>
                            </svg>
                          )},
                          { value: 'squared', label: 'Squared', icon: (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="28" height="28">
                              <rect x="3" y="3" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="2"/>
                              <path fill="currentColor" d="M9 17L12 14 20 22 28 14 31 17 20 28z"/>
                            </svg>
                          )}
                        ].map((style) => (
                          <button
                            key={style.value}
                            type="button"
                            onClick={() => setCurrentMenu({...currentMenu, arrow_style: style.value})}
                            className={`p-3 border-2 rounded-lg text-center transition-all flex flex-col items-center gap-2 ${
                              currentMenu.arrow_style === style.value 
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                                : 'border-slate-200 hover:border-slate-300 text-slate-700'
                            }`}
                          >
                            <div className="flex items-center justify-center">{style.icon}</div>
                            <div className="text-xs">{style.label}</div>
                          </button>
                        ))}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => setCurrentMenu({...currentMenu, arrow_animation: 'none'})}
                          className={`p-3 border-2 rounded-lg text-center transition-all ${
                            currentMenu.arrow_animation === 'none' 
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="font-medium">No Animation</div>
                          <div className="text-xs text-slate-500">Static arrow</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCurrentMenu({...currentMenu, arrow_animation: 'rotate'})}
                          className={`p-3 border-2 rounded-lg text-center transition-all ${
                            currentMenu.arrow_animation === 'rotate' 
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="font-medium">Rotate 180°</div>
                          <div className="text-xs text-slate-500">Arrow flips on hover</div>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Category Grid Specific Settings - Only show for Category Grid */}
                {currentMenu.mega_menu_content_type !== 'expanding_categories' && (
                  <>
                    {/* Mega Menu Width */}
                    <div className="space-y-1">
                      <Label>Mega Menu Width</Label>
                      <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="text"
                      value={currentMenu.mega_menu_width || '80%'}
                      onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_width: e.target.value})}
                      placeholder="e.g., 80%, 1000px"
                    />
                    <Input
                      type="text"
                      value={currentMenu.mega_menu_max_width || '1200px'}
                      onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_max_width: e.target.value})}
                      placeholder="Max width"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Use percentages (80%) or pixels (1000px)</p>
                </div>
                
                {/* Dropdown Border Radius */}
                <div className="space-y-1">
                  <Label>Dropdown Border Radius (px)</Label>
                  <Input
                    type="number"
                    value={currentMenu.mega_menu_border_radius || 8}
                    onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_border_radius: parseInt(e.target.value)})}
                    min="0"
                    max="50"
                    placeholder="8"
                  />
                </div>
                
                {/* Spacing Controls */}
                <div className="space-y-2">
                  <Label>Spacing & Gaps</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Dropdown Padding (px)</Label>
                      <Input
                        type="number"
                        value={currentMenu.mega_menu_dropdown_padding !== undefined ? currentMenu.mega_menu_dropdown_padding : 32}
                        onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_dropdown_padding: parseInt(e.target.value)})}
                        min="0"
                        max="100"
                        placeholder="32"
                      />
                      <p className="text-xs text-slate-500">Top/bottom/sides padding</p>
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">Column Gap (px)</Label>
                      <Input
                        type="number"
                        value={currentMenu.mega_menu_column_gap !== undefined ? currentMenu.mega_menu_column_gap : 32}
                        onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_column_gap: parseInt(e.target.value)})}
                        min="0"
                        max="100"
                        placeholder="32"
                      />
                      <p className="text-xs text-slate-500">Space between columns</p>
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">Row Gap (px)</Label>
                      <Input
                        type="number"
                        value={currentMenu.mega_menu_row_gap !== undefined ? currentMenu.mega_menu_row_gap : 32}
                        onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_row_gap: parseInt(e.target.value)})}
                        min="0"
                        max="100"
                        placeholder="32"
                      />
                      <p className="text-xs text-slate-500">Space between rows</p>
                    </div>
                  </div>
                </div>

                {/* Mega Menu Styling Options */}
                <div className="space-y-3 pt-3 border-t border-slate-200">
                  <h4 className="font-semibold text-slate-700">Mega Menu Styling</h4>
                  
                  {/* Dropdown Background */}
                  <div className="space-y-1">
                    <Label>Dropdown Background Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={currentMenu.mega_menu_dropdown_bg || currentMenu.dropdown_bg || '#ffffff'}
                        onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_dropdown_bg: e.target.value})}
                        className="w-20"
                      />
                      <Input
                        value={currentMenu.mega_menu_dropdown_bg || currentMenu.dropdown_bg || '#ffffff'}
                        onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_dropdown_bg: e.target.value})}
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>
                  
                  {/* Colors */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Root Category Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.mega_menu_root_color || currentMenu.text_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_root_color: e.target.value})}
                          className="w-20"
                        />
                        <Input
                          value={currentMenu.mega_menu_root_color || currentMenu.text_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_root_color: e.target.value})}
                          placeholder="#1f2937"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Subcategory Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.mega_menu_sub_color || currentMenu.dropdown_text_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_sub_color: e.target.value})}
                          className="w-20"
                        />
                        <Input
                          value={currentMenu.mega_menu_sub_color || currentMenu.dropdown_text_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_sub_color: e.target.value})}
                          placeholder="#1f2937"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Root Hover Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.mega_menu_root_hover || currentMenu.dropdown_hover_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_root_hover: e.target.value})}
                          className="w-20"
                        />
                        <Input
                          value={currentMenu.mega_menu_root_hover || currentMenu.dropdown_hover_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_root_hover: e.target.value})}
                          placeholder="#6366f1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Root Hover Background</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.mega_menu_root_hover_bg || currentMenu.dropdown_hover_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_root_hover_bg: e.target.value})}
                          className="w-20"
                        />
                        <Input
                          value={currentMenu.mega_menu_root_hover_bg || currentMenu.dropdown_hover_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_root_hover_bg: e.target.value})}
                          placeholder="#6366f1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Subcategory Hover Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.mega_menu_sub_hover || currentMenu.dropdown_hover_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_sub_hover: e.target.value})}
                          className="w-20"
                        />
                        <Input
                          value={currentMenu.mega_menu_sub_hover || currentMenu.dropdown_hover_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_sub_hover: e.target.value})}
                          placeholder="#6366f1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Subcategory Hover Background</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.mega_menu_sub_hover_bg || currentMenu.dropdown_hover_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_sub_hover_bg: e.target.value})}
                          className="w-20"
                        />
                        <Input
                          value={currentMenu.mega_menu_sub_hover_bg || currentMenu.dropdown_hover_color}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_sub_hover_bg: e.target.value})}
                          placeholder="#6366f1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Default Badge Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.default_label_color || '#ef4444'}
                          onChange={(e) => setCurrentMenu({...currentMenu, default_label_color: e.target.value})}
                          className="w-20"
                        />
                        <Input
                          value={currentMenu.default_label_color || '#ef4444'}
                          onChange={(e) => setCurrentMenu({...currentMenu, default_label_color: e.target.value})}
                          placeholder="#ef4444"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Color for badges (NEW, SALE)</p>
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Label Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.label_text_color || '#64748b'}
                          onChange={(e) => setCurrentMenu({...currentMenu, label_text_color: e.target.value})}
                          className="w-20"
                        />
                        <Input
                          value={currentMenu.label_text_color || '#64748b'}
                          onChange={(e) => setCurrentMenu({...currentMenu, label_text_color: e.target.value})}
                          placeholder="#64748b"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Text color for label items</p>
                    </div>
                  </div>
                  
                  {/* Hover Effect & Animation */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Hover Effect</Label>
                      <select
                        value={currentMenu.mega_menu_hover_effect || 'background'}
                        onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_hover_effect: e.target.value})}
                        className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                      >
                        <option value="background">Background Color</option>
                        <option value="underline">Underline</option>
                        <option value="bold">Bold Text</option>
                        <option value="scale">Scale Up</option>
                      </select>
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Dropdown Animation</Label>
                      <select
                        value={currentMenu.mega_menu_animation || 'fade'}
                        onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_animation: e.target.value})}
                        className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                      >
                        <option value="none">None (Instant)</option>
                        <option value="fade">Fade In</option>
                        <option value="slide">Slide Down</option>
                        <option value="fade-slide">Fade + Slide</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Dividers */}
                  <div className="space-y-2">
                    <div>
                      <label className="flex items-center gap-2 mb-1">
                        <input
                          type="checkbox"
                          checked={currentMenu.mega_menu_row_divider !== false}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_row_divider: e.target.checked})}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm font-medium">Show divider between rows</span>
                      </label>
                      {currentMenu.mega_menu_row_divider !== false && (
                        <div className="ml-6 space-y-1">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Divider Size (px)</Label>
                              <Input
                                type="number"
                                value={currentMenu.mega_menu_row_divider_size || 1}
                                onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_row_divider_size: parseInt(e.target.value)})}
                                min="1"
                                max="10"
                                className="w-24"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Divider Color</Label>
                              <Input
                                type="color"
                                value={currentMenu.mega_menu_row_divider_color || 'rgba(0,0,0,0.1)'}
                                onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_row_divider_color: e.target.value})}
                                className="w-24 h-10"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="flex items-center gap-2 mb-1">
                        <input
                          type="checkbox"
                          checked={currentMenu.mega_menu_category_divider !== false}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_category_divider: e.target.checked})}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm font-medium">Show divider between root and subcategories</span>
                      </label>
                      {currentMenu.mega_menu_category_divider !== false && (
                        <div className="ml-6 space-y-1">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Divider Size (px)</Label>
                              <Input
                                type="number"
                                value={currentMenu.mega_menu_category_divider_size || 2}
                                onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_category_divider_size: parseInt(e.target.value)})}
                                min="1"
                                max="10"
                                className="w-24"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Divider Color</Label>
                              <Input
                                type="color"
                                value={currentMenu.mega_menu_category_divider_color || currentMenu.primary_color}
                                onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_category_divider_color: e.target.value})}
                                className="w-24 h-10"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Layout Options */}
                  <div className="space-y-2">
                    <Label>Layout & Spacing</Label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={currentMenu.mega_menu_root_clickable !== false}
                          onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_root_clickable: e.target.checked})}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm">Make root categories clickable</span>
                      </label>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Subcategory Left Padding (px)</Label>
                          <Input
                            type="number"
                            value={currentMenu.mega_menu_sub_padding || 0}
                            onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_sub_padding: parseInt(e.target.value)})}
                            min="0"
                            max="40"
                          />
                        </div>
                        
                        <div>
                          <label className="flex items-center gap-2 mt-5">
                            <input
                              type="checkbox"
                              checked={currentMenu.mega_menu_center_content || false}
                              onChange={(e) => setCurrentMenu({...currentMenu, mega_menu_center_content: e.target.checked})}
                              className="rounded border-slate-300"
                            />
                            <span className="text-xs">Center content in columns</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Custom Card Styling */}
                  <div className="space-y-3 pt-3 border-t border-slate-200">
                    <h4 className="font-semibold text-slate-700">Custom Card Styling</h4>
                    
                    {/* Colors */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label>Card Title Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={currentMenu.card_title_color || currentMenu.text_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_title_color: e.target.value})}
                            className="w-20"
                          />
                          <Input
                            value={currentMenu.card_title_color || currentMenu.text_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_title_color: e.target.value})}
                            placeholder="#1f2937"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <Label>Description Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={currentMenu.card_description_color || currentMenu.dropdown_text_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_description_color: e.target.value})}
                            className="w-20"
                          />
                          <Input
                            value={currentMenu.card_description_color || currentMenu.dropdown_text_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_description_color: e.target.value})}
                            placeholder="#64748b"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <Label>Button Background</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={currentMenu.card_button_bg_color || currentMenu.primary_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_button_bg_color: e.target.value})}
                            className="w-20"
                          />
                          <Input
                            value={currentMenu.card_button_bg_color || currentMenu.primary_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_button_bg_color: e.target.value})}
                            placeholder="#4f46e5"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <Label>Button Text Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={currentMenu.card_button_text_color || '#ffffff'}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_button_text_color: e.target.value})}
                            className="w-20"
                          />
                          <Input
                            value={currentMenu.card_button_text_color || '#ffffff'}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_button_text_color: e.target.value})}
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <Label>Button Hover Background</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={currentMenu.card_button_hover_bg || currentMenu.card_button_bg_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_button_hover_bg: e.target.value})}
                            className="w-20"
                          />
                          <Input
                            value={currentMenu.card_button_hover_bg || currentMenu.card_button_bg_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_button_hover_bg: e.target.value})}
                            placeholder="#6366f1"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <Label>Button Hover Text Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={currentMenu.card_button_hover_text || currentMenu.card_button_text_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_button_hover_text: e.target.value})}
                            className="w-20"
                          />
                          <Input
                            value={currentMenu.card_button_hover_text || currentMenu.card_button_text_color}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_button_hover_text: e.target.value})}
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Dimensions & Spacing */}
                    <div>
                      <Label>Image Size & Layout</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Image Width</Label>
                          <Input
                            value={currentMenu.card_image_width || '100%'}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_image_width: e.target.value})}
                            placeholder="100% or 200px"
                          />
                          <p className="text-xs text-slate-500">Use % or px</p>
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">Image Height (px)</Label>
                          <Input
                            type="number"
                            value={currentMenu.card_image_height && currentMenu.card_image_height !== 'auto' ? parseInt(currentMenu.card_image_height) : ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setCurrentMenu({
                                ...currentMenu, 
                                card_image_height: val ? `${val}px` : 'auto'
                              });
                            }}
                            placeholder="Leave empty for auto"
                            min="0"
                          />
                          <p className="text-xs text-slate-500">Fixed height for consistency</p>
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">Card Padding (px)</Label>
                          <Input
                            type="number"
                            value={currentMenu.card_padding !== undefined ? currentMenu.card_padding : 20}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_padding: parseInt(e.target.value)})}
                            min="0"
                            max="50"
                          />
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">Border Radius (px)</Label>
                          <Input
                            type="number"
                            value={currentMenu.card_border_radius !== undefined ? currentMenu.card_border_radius : 8}
                            onChange={(e) => setCurrentMenu({...currentMenu, card_border_radius: parseInt(e.target.value)})}
                            min="0"
                            max="50"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Category List Styling */}
                  <div className="space-y-3 pt-3 border-t border-slate-200">
                    <h4 className="font-semibold text-slate-700">Category List Styling</h4>
                    
                    <div>
                      <Label>Spacing & Layout</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Padding (px)</Label>
                          <Input
                            type="number"
                            value={currentMenu.category_list_padding !== undefined ? currentMenu.category_list_padding : 0}
                            onChange={(e) => setCurrentMenu({...currentMenu, category_list_padding: parseInt(e.target.value)})}
                            min="0"
                            max="50"
                          />
                          <p className="text-xs text-slate-500">Inner padding for category lists</p>
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">Border Radius (px)</Label>
                          <Input
                            type="number"
                            value={currentMenu.category_list_border_radius !== undefined ? currentMenu.category_list_border_radius : 0}
                            onChange={(e) => setCurrentMenu({...currentMenu, category_list_border_radius: parseInt(e.target.value)})}
                            min="0"
                            max="50"
                          />
                          <p className="text-xs text-slate-500">Corner rounding for category lists</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                  </>
                )}
              </>
            )}
          </div>
          )}
          
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Menu'}
          </Button>

        </TabsContent>
        )}

        {/* Design Tab */}
        <TabsContent value="design" className="space-y-4">
          <div className="card space-y-6">
            <h3 className="text-lg font-semibold">Menu Design</h3>


            {/* Font Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Font Family</Label>
                <select
                  value={currentMenu.font_family}
                  onChange={(e) => setCurrentMenu({...currentMenu, font_family: e.target.value})}
                  className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                  style={{ fontFamily: currentMenu.font_family }}
                >
                  <option value="Inter, sans-serif" style={{fontFamily: 'Inter, sans-serif'}}>Inter</option>
                  <option value="'Outfit', sans-serif" style={{fontFamily: "'Outfit', sans-serif"}}>Outfit</option>
                  <option value="'Roboto', sans-serif" style={{fontFamily: "'Roboto', sans-serif"}}>Roboto</option>
                  <option value="'Open Sans', sans-serif" style={{fontFamily: "'Open Sans', sans-serif"}}>Open Sans</option>
                  <option value="'Lato', sans-serif" style={{fontFamily: "'Lato', sans-serif"}}>Lato</option>
                  <option value="'Montserrat', sans-serif" style={{fontFamily: "'Montserrat', sans-serif"}}>Montserrat</option>
                  <option value="'Poppins', sans-serif" style={{fontFamily: "'Poppins', sans-serif"}}>Poppins</option>
                  <option value="'Raleway', sans-serif" style={{fontFamily: "'Raleway', sans-serif"}}>Raleway</option>
                  <option value="'Nunito', sans-serif" style={{fontFamily: "'Nunito', sans-serif"}}>Nunito</option>
                  <option value="'Work Sans', sans-serif" style={{fontFamily: "'Work Sans', sans-serif"}}>Work Sans</option>
                  <option value="'Source Sans Pro', sans-serif" style={{fontFamily: "'Source Sans Pro', sans-serif"}}>Source Sans Pro</option>
                  <option value="'Ubuntu', sans-serif" style={{fontFamily: "'Ubuntu', sans-serif"}}>Ubuntu</option>
                  <option value="'Oswald', sans-serif" style={{fontFamily: "'Oswald', sans-serif"}}>Oswald</option>
                  <option value="'Quicksand', sans-serif" style={{fontFamily: "'Quicksand', sans-serif"}}>Quicksand</option>
                  <option value="'Playfair Display', serif" style={{fontFamily: "'Playfair Display', serif"}}>Playfair Display</option>
                  <option value="'Merriweather', serif" style={{fontFamily: "'Merriweather', serif"}}>Merriweather</option>
                  <option value="'Lora', serif" style={{fontFamily: "'Lora', serif"}}>Lora</option>
                  <option value="'PT Serif', serif" style={{fontFamily: "'PT Serif', serif"}}>PT Serif</option>
                  <option value="Georgia, serif" style={{fontFamily: 'Georgia, serif'}}>Georgia</option>
                  <option value="'Courier New', monospace" style={{fontFamily: "'Courier New', monospace"}}>Courier New</option>
                  <option value="Arial, sans-serif" style={{fontFamily: 'Arial, sans-serif'}}>Arial</option>
                  <option value="'Times New Roman', serif" style={{fontFamily: "'Times New Roman', serif"}}>Times New Roman</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Font Size (px)</Label>
                <Input
                  type="number"
                  value={currentMenu.font_size}
                  onChange={(e) => setCurrentMenu({...currentMenu, font_size: parseInt(e.target.value)})}
                />
              </div>
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={currentMenu.primary_color}
                    onChange={(e) => setCurrentMenu({...currentMenu, primary_color: e.target.value})}
                    className="w-20"
                  />
                  <Input
                    value={currentMenu.primary_color}
                    onChange={(e) => setCurrentMenu({...currentMenu, primary_color: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Hover Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={currentMenu.hover_color}
                    onChange={(e) => setCurrentMenu({...currentMenu, hover_color: e.target.value})}
                    className="w-20"
                  />
                  <Input
                    value={currentMenu.hover_color}
                    onChange={(e) => setCurrentMenu({...currentMenu, hover_color: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={currentMenu.background_color}
                    onChange={(e) => setCurrentMenu({...currentMenu, background_color: e.target.value})}
                    className="w-20"
                  />
                  <Input
                    value={currentMenu.background_color}
                    onChange={(e) => setCurrentMenu({...currentMenu, background_color: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Text Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={currentMenu.text_color}
                    onChange={(e) => setCurrentMenu({...currentMenu, text_color: e.target.value})}
                    className="w-20"
                  />
                  <Input
                    value={currentMenu.text_color}
                    onChange={(e) => setCurrentMenu({...currentMenu, text_color: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Dropdown Background</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={currentMenu.dropdown_bg}
                    onChange={(e) => setCurrentMenu({...currentMenu, dropdown_bg: e.target.value})}
                    className="w-20"
                  />
                  <Input
                    value={currentMenu.dropdown_bg}
                    onChange={(e) => setCurrentMenu({...currentMenu, dropdown_bg: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Dropdown Text Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={currentMenu.dropdown_text_color || '#1f2937'}
                    onChange={(e) => setCurrentMenu({...currentMenu, dropdown_text_color: e.target.value})}
                    className="w-20"
                  />
                  <Input
                    value={currentMenu.dropdown_text_color || '#1f2937'}
                    onChange={(e) => setCurrentMenu({...currentMenu, dropdown_text_color: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Dropdown Hover Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={currentMenu.dropdown_hover_color || '#6366f1'}
                    onChange={(e) => setCurrentMenu({...currentMenu, dropdown_hover_color: e.target.value})}
                    className="w-20"
                  />
                  <Input
                    value={currentMenu.dropdown_hover_color || '#6366f1'}
                    onChange={(e) => setCurrentMenu({...currentMenu, dropdown_hover_color: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Vertical Menu Specific Colors */}
            {currentMenu.menu_type === 'vertical' && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                <div className="space-y-2">
                  <Label>Column Background</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentMenu.vertical_column_bg || '#ffffff'}
                      onChange={(e) => setCurrentMenu(prev => ({...prev, vertical_column_bg: e.target.value}))}
                      className="w-20"
                    />
                    <Input
                      value={currentMenu.vertical_column_bg || '#ffffff'}
                      onChange={(e) => setCurrentMenu(prev => ({...prev, vertical_column_bg: e.target.value}))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Selected Category Background</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentMenu.vertical_selected_bg || '#e0e7ff'}
                      onChange={(e) => setCurrentMenu(prev => ({...prev, vertical_selected_bg: e.target.value}))}
                      className="w-20"
                    />
                    <Input
                      value={currentMenu.vertical_selected_bg || '#e0e7ff'}
                      onChange={(e) => setCurrentMenu(prev => ({...prev, vertical_selected_bg: e.target.value}))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Hover Background</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentMenu.vertical_hover_bg || '#f3f4f6'}
                      onChange={(e) => setCurrentMenu(prev => ({...prev, vertical_hover_bg: e.target.value}))}
                      className="w-20"
                    />
                    <Input
                      value={currentMenu.vertical_hover_bg || '#f3f4f6'}
                      onChange={(e) => setCurrentMenu(prev => ({...prev, vertical_hover_bg: e.target.value}))}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Dropdown Animation */}
            <div className="space-y-2">
              <Label>Dropdown Animation Style</Label>
              <select
                value={currentMenu.animation_style}
                onChange={(e) => setCurrentMenu({...currentMenu, animation_style: e.target.value})}
                className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
              >
                <option value="fade">Fade</option>
                <option value="slide">Slide</option>
                <option value="none">None</option>
              </select>
              <p className="text-xs text-slate-500">Animation when dropdown menus appear</p>
            </div>

            {/* Spacing & Layout */}
            <div className="space-y-4 pt-4 border-t border-slate-200">
              <h4 className="font-semibold text-slate-700">Spacing & Layout</h4>
              
              <div className="space-y-2">
                <Label>Border Radius (px)</Label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="24"
                    value={currentMenu.border_radius ?? 8}
                    onChange={(e) => setCurrentMenu({...currentMenu, border_radius: parseInt(e.target.value)})}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={currentMenu.border_radius ?? 8}
                    onChange={(e) => setCurrentMenu({...currentMenu, border_radius: parseInt(e.target.value)})}
                    className="w-20"
                  />
                </div>
                <div className="text-xs text-slate-500">Set to 0 for sharp corners (embedded menus)</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Horizontal Padding (px)</Label>
                  <Input
                    type="number"
                    value={currentMenu.padding_x ?? 20}
                    onChange={(e) => setCurrentMenu({...currentMenu, padding_x: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vertical Padding (px)</Label>
                  <Input
                    type="number"
                    value={currentMenu.padding_y ?? 12}
                    onChange={(e) => setCurrentMenu({...currentMenu, padding_y: parseInt(e.target.value)})}
                  />
                </div>
              </div>
            </div>

            {/* Icon & Arrow Settings */}
            <div className="space-y-4 pt-4 border-t border-slate-200">
              <h4 className="font-semibold text-slate-700">Icon & Arrow Settings</h4>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentMenu.show_icons}
                  onChange={(e) => setCurrentMenu({...currentMenu, show_icons: e.target.checked})}
                  className="w-5 h-5"
                />
                <div>
                  <div className="font-medium">Show Icons</div>
                  <div className="text-sm text-slate-500">Display icons next to menu items</div>
                </div>
              </label>

              {/* Show Dropdown Arrows - Only for horizontal menus */}
              {currentMenu.menu_type !== 'vertical' && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentMenu.show_dropdown_arrows}
                  onChange={(e) => setCurrentMenu({...currentMenu, show_dropdown_arrows: e.target.checked})}
                  className="w-5 h-5"
                />
                <div>
                  <div className="font-medium">Show Dropdown Arrows</div>
                  <div className="text-sm text-slate-500">Display arrows for items with submenus</div>
                </div>
              </label>
              )}

              {/* Arrow Style - Only shown if arrows are enabled and horizontal menu */}
              {currentMenu.menu_type !== 'vertical' && currentMenu.show_dropdown_arrows && (
                <>
                  <div className="space-y-2">
                    <Label>Arrow Style</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { value: 'chevron', label: 'Chevron', icon: (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="24" height="24">
                            <path fill="currentColor" d="M38,8.064v10.452l-18,14.21L2,18.515V8.064l17.38,13.721L20,22.274l0.62-0.489L38,8.064 M39,6L20,21L1,6v13l19,15l19-15V6L39,6z"/>
                          </svg>
                        )},
                        { value: 'circled', label: 'Circled', icon: (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="28" height="28">
                            <circle cx="20" cy="20" r="17" fill="none" stroke="currentColor" strokeWidth="2"/>
                            <path fill="currentColor" d="M9 17L12 14 20 22 28 14 31 17 20 28z"/>
                          </svg>
                        )},
                        { value: 'double', label: 'Double', icon: (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="24" height="24">
                            <path fill="currentColor" d="M9 11L12 8 20 16 28 8 31 11 20 22z"/>
                            <path fill="currentColor" d="M9 21L12 18 20 26 28 18 31 21 20 32z"/>
                          </svg>
                        )},
                        { value: 'squared', label: 'Squared', icon: (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="28" height="28">
                            <rect x="3" y="3" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="2"/>
                            <path fill="currentColor" d="M9 17L12 14 20 22 28 14 31 17 20 28z"/>
                          </svg>
                        )}
                      ].map((style) => (
                        <button
                          key={style.value}
                          type="button"
                          onClick={() => setCurrentMenu({...currentMenu, arrow_style: style.value})}
                          className={`p-3 border-2 rounded-lg text-center transition-all flex flex-col items-center gap-2 ${
                            currentMenu.arrow_style === style.value 
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                              : 'border-slate-200 hover:border-slate-300 text-slate-700'
                          }`}
                        >
                          <div className="flex items-center justify-center">{style.icon}</div>
                          <div className="text-xs">{style.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Arrow Animation</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setCurrentMenu({...currentMenu, arrow_animation: 'none'})}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          currentMenu.arrow_animation === 'none' 
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="font-medium">None</div>
                        <div className="text-xs text-slate-500">No animation</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentMenu({...currentMenu, arrow_animation: 'rotate'})}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          currentMenu.arrow_animation === 'rotate' 
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="font-medium">Rotate 180°</div>
                        <div className="text-xs text-slate-500">Arrow flips on hover</div>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Menu'}
          </Button>

        </TabsContent>

        {/* Mobile Tab */}
        <TabsContent value="mobile" className="space-y-4">
          <div className="card space-y-6">
            <h3 className="text-lg font-semibold">Mobile Menu Design</h3>
            
            <p className="text-sm text-slate-600 mb-4">
              Mobile menu uses the same categories and items as your desktop menu. Configure your menu structure in the Categories and Menu Placement tabs.
            </p>
            
            {/* Mobile Menu Item Ordering */}
            <div className="space-y-4 p-4 bg-slate-50 border border-slate-200 rounded-lg mb-6">
              <h4 className="font-medium text-slate-700">Mobile Menu Order</h4>
              <p className="text-xs text-slate-500">Drag to reorder how items appear in the mobile menu</p>
              
              <div className="space-y-2">
                {(() => {
                  // Build ordered list of mobile menu items
                  const includedIds = new Set(currentMenu.included_categories || []);
                  const headerCats = categoryHierarchy.filter(cat => includedIds.has(cat.id));
                  const customItemsFiltered = (currentMenu.custom_items || []).filter(item => !item.is_button);
                  
                  let allItems = [];
                  
                  // Add categories
                  headerCats.forEach(cat => {
                    allItems.push({ type: 'category', id: cat.id, name: cat.name, data: cat });
                  });
                  
                  // Add mega menu if enabled
                  if (currentMenu.mega_menu_enabled && currentMenu.mega_menu_rows?.length > 0) {
                    allItems.push({ 
                      type: 'megamenu', 
                      id: 'megamenu', 
                      name: currentMenu.mega_menu_button?.label || 'Shop' 
                    });
                  }
                  
                  // Add custom items
                  customItemsFiltered.forEach((item, idx) => {
                    allItems.push({ 
                      type: 'custom', 
                      id: `custom-${idx}`, 
                      name: item.mobile_label || item.label || 'Custom Item',
                      data: item 
                    });
                  });
                  
                  // Sort items based on mobile_item_order if it exists
                  if (currentMenu.mobile_item_order && currentMenu.mobile_item_order.length > 0) {
                    const orderMap = new Map(currentMenu.mobile_item_order.map((id, idx) => [id, idx]));
                    allItems.sort((a, b) => {
                      const orderA = orderMap.has(a.id) ? orderMap.get(a.id) : 999;
                      const orderB = orderMap.has(b.id) ? orderMap.get(b.id) : 999;
                      return orderA - orderB;
                    });
                  }
                  
                  return allItems.map((item, index) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded border border-slate-200">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400">::</span>
                        <span className="text-sm font-medium">{item.name}</span>
                        <span className="text-xs text-slate-500">
                          ({item.type === 'category' ? '📂 Category' : item.type === 'megamenu' ? '⚡ Mega Menu' : '🔗 Link'})
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            if (index > 0) {
                              // Get current mobile_item_order or create from current state
                              const currentOrder = currentMenu.mobile_item_order || allItems.map(i => i.id);
                              const newOrder = [...currentOrder];
                              // Swap with previous
                              [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                              setCurrentMenu({...currentMenu, mobile_item_order: newOrder});
                            }
                          }}
                          disabled={index === 0}
                          className="p-1 hover:bg-slate-100 rounded disabled:opacity-30 text-sm"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => {
                            if (index < allItems.length - 1) {
                              // Get current mobile_item_order or create from current state
                              const currentOrder = currentMenu.mobile_item_order || allItems.map(i => i.id);
                              const newOrder = [...currentOrder];
                              // Swap with next
                              [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                              setCurrentMenu({...currentMenu, mobile_item_order: newOrder});
                            }
                          }}
                          disabled={index === allItems.length - 1}
                          className="p-1 hover:bg-slate-100 rounded disabled:opacity-30 text-sm"
                          title="Move down"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Mobile Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700">Mobile Display Settings</h4>
                
                <div className="space-y-2">
                  <Label>Hamburger Icon Position</Label>
                  <select 
                    value={currentMenu?.mobile_hamburger_position || 'right'} 
                    onChange={(e) => {
                      console.log('Hamburger position changed to:', e.target.value);
                      setCurrentMenu(prev => ({...prev, mobile_hamburger_position: e.target.value}));
                    }}
                    className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm hover:bg-slate-50 cursor-pointer"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                  <p className="text-xs text-slate-500">
                    Position of hamburger menu icon before menu opens
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Menu Position</Label>
                  <select 
                    value={currentMenu?.mobile_menu_position || 'left'} 
                    onChange={(e) => {
                      console.log('Mobile menu position changed to:', e.target.value);
                      setCurrentMenu(prev => ({...prev, mobile_menu_position: e.target.value}));
                    }}
                    className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm hover:bg-slate-50 cursor-pointer"
                  >
                    <option value="left">Slide from Left</option>
                    <option value="right">Slide from Right</option>
                    <option value="center">Center (Full Width)</option>
                  </select>
                  <p className="text-xs text-slate-500">
                    Direction menu slides in from when opened
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Menu Style</Label>
                  <select 
                    value={currentMenu?.mobile_style || 'hamburger'} 
                    onChange={(e) => setCurrentMenu(prev => ({...prev, mobile_style: e.target.value}))}
                    className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm hover:bg-slate-50 cursor-pointer"
                  >
                    <option value="hamburger">Hamburger Menu</option>
                    <option value="drawer">Side Drawer</option>
                    <option value="slide">Slide Down</option>
                  </select>
                  <p className="text-xs text-slate-500">
                    Style of mobile menu display (currently only Hamburger is fully implemented)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Background Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentMenu.mobile_background_color || currentMenu.background_color || '#ffffff'}
                      onChange={(e) => setCurrentMenu({...currentMenu, mobile_background_color: e.target.value})}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={currentMenu.mobile_background_color || currentMenu.background_color || '#ffffff'}
                      onChange={(e) => setCurrentMenu({...currentMenu, mobile_background_color: e.target.value})}
                      placeholder="#ffffff"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Defaults to main menu background color if not set
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Text Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentMenu.mobile_text_color || currentMenu.text_color || '#000000'}
                      onChange={(e) => setCurrentMenu({...currentMenu, mobile_text_color: e.target.value})}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={currentMenu.mobile_text_color || currentMenu.text_color || '#000000'}
                      onChange={(e) => setCurrentMenu({...currentMenu, mobile_text_color: e.target.value})}
                      placeholder="#000000"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Defaults to main menu text color if not set
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Breakpoint</Label>
                  <Input
                    type="number"
                    value={currentMenu.mobile_breakpoint || 768}
                    onChange={(e) => setCurrentMenu({...currentMenu, mobile_breakpoint: parseInt(e.target.value)})}
                    placeholder="768"
                  />
                  <p className="text-xs text-slate-500">
                    Menu switches to mobile view below this width (in pixels). Default: 768px
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Menu Item Padding (px)</Label>
                  <Input
                    type="number"
                    value={currentMenu.mobile_menu_padding || 12}
                    onChange={(e) => setCurrentMenu({...currentMenu, mobile_menu_padding: parseInt(e.target.value) || 12})}
                    placeholder="12"
                    min="4"
                    max="32"
                  />
                  <p className="text-xs text-slate-500">
                    Vertical padding for mobile menu items (default: 12px)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Hamburger Icon Padding (px)</Label>
                  <Input
                    type="number"
                    value={currentMenu.mobile_hamburger_padding || 12}
                    onChange={(e) => setCurrentMenu({...currentMenu, mobile_hamburger_padding: parseInt(e.target.value) || 12})}
                    placeholder="12"
                    min="0"
                    max="40"
                  />
                  <p className="text-xs text-slate-500">
                    Vertical padding around hamburger icon when closed (default: 12px)
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <h5 className="font-medium text-slate-700 mb-3">Mobile Mega Menu Styling</h5>
                  
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Category Header Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.mobile_mega_header_color || currentMenu.mega_menu_root_color || currentMenu.text_color || '#000000'}
                          onChange={(e) => setCurrentMenu({...currentMenu, mobile_mega_header_color: e.target.value})}
                          className="w-16 h-8"
                        />
                        <Input
                          type="text"
                          value={currentMenu.mobile_mega_header_color || currentMenu.mega_menu_root_color || currentMenu.text_color || '#000000'}
                          onChange={(e) => setCurrentMenu({...currentMenu, mobile_mega_header_color: e.target.value})}
                          className="text-xs"
                          placeholder="#000000"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Subcategory Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.mobile_mega_sub_color || currentMenu.mega_menu_sub_color || '#666666'}
                          onChange={(e) => setCurrentMenu({...currentMenu, mobile_mega_sub_color: e.target.value})}
                          className="w-16 h-8"
                        />
                        <Input
                          type="text"
                          value={currentMenu.mobile_mega_sub_color || currentMenu.mega_menu_sub_color || '#666666'}
                          onChange={(e) => setCurrentMenu({...currentMenu, mobile_mega_sub_color: e.target.value})}
                          className="text-xs"
                          placeholder="#666666"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Card Background Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentMenu.mobile_mega_card_bg || '#f8f9fa'}
                          onChange={(e) => setCurrentMenu({...currentMenu, mobile_mega_card_bg: e.target.value})}
                          className="w-16 h-8"
                        />
                        <Input
                          type="text"
                          value={currentMenu.mobile_mega_card_bg || '#f8f9fa'}
                          onChange={(e) => setCurrentMenu({...currentMenu, mobile_mega_card_bg: e.target.value})}
                          className="text-xs"
                          placeholder="#f8f9fa"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Card Image Height (px)</Label>
                      <Input
                        type="number"
                        value={currentMenu.mobile_mega_image_height || 64}
                        onChange={(e) => setCurrentMenu({...currentMenu, mobile_mega_image_height: parseInt(e.target.value) || 64})}
                        className="text-xs"
                        placeholder="64"
                        min="32"
                        max="200"
                      />
                      <p className="text-xs text-slate-500">
                        Height of card images in mobile mega menu (default: 64px)
                      </p>
                    </div>
                  </div>
                </div>
                
                <Button onClick={handleSave} disabled={saving} className="w-full">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Mobile Settings'}
                </Button>
              </div>
              
              {/* Mobile Preview */}
              <div className="space-y-2">
                <h4 className="font-medium text-slate-700">Mobile Preview</h4>
                <div className="border-2 border-slate-300 rounded-3xl p-4 bg-slate-50 mx-auto" style={{width: '375px', height: '667px'}}>
                  <div className="bg-white rounded-2xl h-full overflow-hidden shadow-lg flex flex-col">
                    {/* Mobile Status Bar */}
                    <div className="bg-slate-800 text-white text-xs px-4 py-1 flex justify-between items-center">
                      <span>9:41</span>
                      <div className="flex gap-1">
                        <span>📶</span>
                        <span>📡</span>
                        <span>🔋</span>
                      </div>
                    </div>
                    
                    {/* Mobile Navigation Preview */}
                    <div 
                      className="overflow-y-auto flex-1"
                      style={{
                        backgroundColor: currentMenu.background_color || '#ffffff',
                        color: currentMenu.text_color || '#000000',
                        fontSize: (currentMenu.font_size || 14) + 'px',
                        fontFamily: currentMenu.font_family || 'sans-serif'
                      }}
                    >
                      {/* Hamburger Header */}
                      <div className="flex justify-between items-center p-4 border-b border-slate-200">
                        <span className="font-semibold">Menu</span>
                        <button className="text-2xl">☰</button>
                      </div>
                      
                      {/* Mobile Menu Items - Interactive Preview */}
                      <div className="p-2">
                        {(() => {
                          // Build ordered list of mobile menu items for preview
                          const includedIds = new Set(currentMenu.included_categories || []);
                          const headerCats = categoryHierarchy.filter(cat => includedIds.has(cat.id));
                          const customItemsFiltered = (currentMenu.custom_items || []).filter(item => !item.is_button);
                          
                          let allItems = [];
                          
                          // Add categories
                          headerCats.forEach(cat => {
                            allItems.push({ type: 'category', id: cat.id, data: cat });
                          });
                          
                          // Add mega menu if enabled
                          if (currentMenu.mega_menu_enabled && currentMenu.mega_menu_rows?.length > 0) {
                            allItems.push({ type: 'megamenu', id: 'megamenu' });
                          }
                          
                          // Add custom items
                          customItemsFiltered.forEach((item, idx) => {
                            allItems.push({ type: 'custom', id: `custom-${idx}`, data: item });
                          });
                          
                          // Sort items based on mobile_item_order if it exists
                          if (currentMenu.mobile_item_order && currentMenu.mobile_item_order.length > 0) {
                            const orderMap = new Map(currentMenu.mobile_item_order.map((id, idx) => [id, idx]));
                            allItems.sort((a, b) => {
                              const orderA = orderMap.has(a.id) ? orderMap.get(a.id) : 999;
                              const orderB = orderMap.has(b.id) ? orderMap.get(b.id) : 999;
                              return orderA - orderB;
                            });
                          }
                          
                          return allItems.map((item) => {
                            if (item.type === 'category') {
                              const cat = item.data;
                              const isExpanded = expandedMobileItems.has(cat.id);
                              return (
                                <div key={cat.id} className="border-b border-slate-100">
                                  <div 
                                    className="flex justify-between items-center p-3 cursor-pointer"
                                    onClick={() => {
                                      const newExpanded = new Set(expandedMobileItems);
                                      if (isExpanded) {
                                        newExpanded.delete(cat.id);
                                      } else {
                                        newExpanded.add(cat.id);
                                      }
                                      setExpandedMobileItems(newExpanded);
                                    }}
                                  >
                                    <span>{cat.name}</span>
                                    {cat.children && cat.children.length > 0 && (
                                      <span className="text-xs transition-transform" style={{transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}}>▼</span>
                                    )}
                                  </div>
                                  {/* Subcategories */}
                                  {isExpanded && cat.children && cat.children.length > 0 && (
                                    <div className="bg-slate-50 pl-6 py-2">
                                      {cat.children.map(child => (
                                        <div key={child.id} className="py-2 text-sm text-slate-600">
                                          {child.name}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            } else if (item.type === 'custom') {
                              const customItem = item.data;
                              return (
                                <div key={item.id} className="border-b border-slate-100">
                                  <div className="p-3">
                                    <span>{customItem.mobile_label || customItem.label || 'Link'}</span>
                                  </div>
                                </div>
                              );
                            } else if (item.type === 'megamenu') {
                              const isExpanded = expandedMobileItems.has('megamenu');
                              return (
                                <div key="megamenu" className="border-b border-slate-100">
                                  <div 
                                    className="flex justify-between items-center p-3 font-semibold cursor-pointer"
                                    onClick={() => {
                                      const newExpanded = new Set(expandedMobileItems);
                                      if (isExpanded) {
                                        newExpanded.delete('megamenu');
                                      } else {
                                        newExpanded.add('megamenu');
                                      }
                                      setExpandedMobileItems(newExpanded);
                                    }}
                                  >
                                    <span>{currentMenu.mega_menu_button?.label || 'Shop'}</span>
                                    <span className="transition-transform" style={{transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}}>▼</span>
                                  </div>
                                  {/* Mega Menu Content */}
                                  {isExpanded && (
                                    <div className="p-3 space-y-3">
                                      {currentMenu.mega_menu_rows.map((row, rowIdx) => (
                                        <div key={rowIdx} className="space-y-2">
                                          {row.columns && row.columns.map((col, colIdx) => (
                                            <div key={colIdx} className="text-sm">
                                              {col.type === 'category_list' && (() => {
                                                const rootCat = categoryHierarchy.find(c => c.id === col.root_category_id);
                                                const headerColor = currentMenu.mobile_mega_header_color || currentMenu.mega_menu_root_color || currentMenu.text_color || '#000';
                                                const subColor = currentMenu.mobile_mega_sub_color || currentMenu.mega_menu_sub_color || '#666';
                                                return rootCat ? (
                                                  <div>
                                                    <div className="font-semibold mb-1" style={{color: headerColor}}>{rootCat.name}</div>
                                                    {rootCat.children && rootCat.children.slice(0, 3).map(child => (
                                                      <div key={child.id} className="pl-3 py-1" style={{color: subColor}}>{child.name}</div>
                                                    ))}
                                                    {rootCat.children && rootCat.children.length > 3 && (
                                                      <div className="pl-3 text-xs text-slate-400">+ {rootCat.children.length - 3} more</div>
                                                    )}
                                                  </div>
                                                ) : null;
                                              })()}
                                              {col.type === 'card' && (
                                                <div className="p-2 rounded" style={{backgroundColor: currentMenu.mobile_mega_card_bg || currentMenu.card_bg_color || '#f8f9fa'}}>
                                                  {col.image && (
                                                    <img 
                                                      src={col.image} 
                                                      alt={col.title || ''} 
                                                      className="w-full object-cover rounded mb-2" 
                                                      style={{height: `${currentMenu.mobile_mega_image_height || 64}px`}}
                                                    />
                                                  )}
                                                  {col.title && <div className="font-semibold text-xs" style={{color: currentMenu.card_title_color || currentMenu.text_color || '#000'}}>{col.title}</div>}
                                                  {col.description && <div className="text-xs mt-1" style={{color: currentMenu.card_description_color || currentMenu.dropdown_text_color || '#666'}}>{col.description}</div>}
                                                  {col.button_text && (
                                                    <div className="text-xs mt-1" style={{color: currentMenu.card_button_text_color || '#ffffff', backgroundColor: currentMenu.card_button_bg_color || currentMenu.primary_color || '#4f46e5', padding: '4px 8px', borderRadius: '4px', display: 'inline-block'}}>{col.button_text}</div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ))}
                                      <div className="text-xs text-slate-500 text-center pt-2 border-t border-slate-200">
                                        Configure columns in the Mega Menu tab
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Preview of mobile menu (iPhone 8 size: 375×667px)
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Embed Code Tab */}
        <TabsContent value="code" className="space-y-4">
          <div className="card space-y-4">
            <h3 className="text-lg font-semibold">Embed Code</h3>
            <p className="text-slate-600">
              Copy this code and paste it into your website where you want the navigation menu to appear.
            </p>

            {/* Root Domain for Category URLs */}
            <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="font-semibold text-blue-900">Website Root Domain</Label>
              <Input
                type="text"
                value={currentMenu.root_domain || ''}
                onChange={(e) => setCurrentMenu({...currentMenu, root_domain: e.target.value})}
                placeholder="https://yourwebsite.com"
              />
              <p className="text-xs text-blue-700">
                Enter your website's root domain. Category URLs will be generated as: <strong>{currentMenu.root_domain || 'https://yourwebsite.com'}/products-list/collections/[category-slug]</strong>
              </p>
              
              {/* GoHighLevel Warning */}
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-md">
                <p className="text-xs font-semibold text-amber-900 mb-1">⚠️ Important for GoHighLevel Websites:</p>
                <p className="text-xs text-amber-800 mb-2">
                  GoHighLevel does NOT support "www." in collection URLs. Remove "www." from your domain or links will not work.
                </p>
                <div className="space-y-1 text-xs">
                  <p className="text-red-700">❌ Wrong: <code className="bg-white px-1 py-0.5 rounded">https://www.yoursite.com</code></p>
                  <p className="text-green-700">✅ Correct: <code className="bg-white px-1 py-0.5 rounded">https://yoursite.com</code></p>
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Domain'}
            </Button>

            <Button onClick={handleGenerateCode}>
              <Code className="w-4 h-4 mr-2" />
              Generate Embed Code
            </Button>

            {embedCode && (
              <>
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
                    <code>{embedCode}</code>
                  </pre>
                  <Button
                    size="sm"
                    className="absolute top-2 right-2 bg-slate-700 text-white hover:bg-white hover:text-slate-900 border-2 border-slate-700"
                    onClick={handleCopyCode}
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Code
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Menu Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Menu</DialogTitle>
            <DialogDescription>Enter a name and select the type for your new navigation menu</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Menu Name</Label>
              <Input
                value={newMenuName}
                onChange={(e) => setNewMenuName(e.target.value)}
                placeholder="e.g., Header Menu, Footer Menu"
                onKeyPress={(e) => e.key === 'Enter' && handleCreateMenu()}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Menu Type</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="menuType"
                    value="horizontal"
                    checked={newMenuType === 'horizontal'}
                    onChange={(e) => setNewMenuType(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Horizontal Menu</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="menuType"
                    value="footer"
                    checked={newMenuType === 'footer'}
                    onChange={(e) => setNewMenuType(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Footer Menu</span>
                </label>
              </div>
              <p className="text-xs text-slate-500">
                {newMenuType === 'horizontal' 
                  ? 'Traditional navigation bar with dropdowns and mega menus'
                  : 'Multi-column footer navigation for site structure and links'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateMenu}>Create Menu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Row Dialog */}
      <Dialog open={showAddRowDialog} onOpenChange={setShowAddRowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Row</DialogTitle>
            <DialogDescription>How many columns would you like in this row?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Number of Columns</Label>
              <div className="flex items-center gap-4">
                {[2, 3, 4, 5, 6].map(num => (
                  <button
                    key={num}
                    onClick={() => setNewRowColumns(num)}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      newRowColumns === num 
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-semibold' 
                        : 'border-slate-300 hover:border-indigo-400'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">You can add or remove columns later</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRowDialog(false)}>Cancel</Button>
            <Button onClick={() => {
              const newRow = {
                columns: []
              };
              for (let i = 0; i < newRowColumns; i++) {
                newRow.columns.push({type: 'category_list', root_category_id: '', auto_include_subcategories: true});
              }
              setCurrentMenu({
                ...currentMenu, 
                mega_menu_rows: [...(currentMenu.mega_menu_rows || []), newRow]
              });
              setShowAddRowDialog(false);
            }}>
              Add Row
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Column Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Column</DialogTitle>
            <DialogDescription>Select which row to duplicate this column to</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Target Row</Label>
              <Select 
                value={duplicateTargetRow !== null ? String(duplicateTargetRow) : ''} 
                onValueChange={(val) => setDuplicateTargetRow(parseInt(val))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select row" />
                </SelectTrigger>
                <SelectContent>
                  {(currentMenu.mega_menu_rows || []).map((row, idx) => (
                    <SelectItem key={idx} value={String(idx)}>
                      Row {idx + 1} ({(row.columns || []).length} columns)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>Cancel</Button>
            <Button onClick={confirmDuplicateColumn}>
              <Files className="w-4 h-4 mr-2" />
              Duplicate to Row
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Menu Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Menu</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this menu? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMenu} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Row Confirmation */}
      <AlertDialog open={showDeleteRowDialog} onOpenChange={setShowDeleteRowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Row</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this row? All columns in this row will be removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteRow} className="bg-red-600 hover:bg-red-700">
              Delete Row
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Column Confirmation */}
      <AlertDialog open={showRemoveColumnDialog} onOpenChange={setShowRemoveColumnDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Column</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the last column from this row? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveColumn} className="bg-orange-600 hover:bg-orange-700">
              Remove Column
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Style Dialog */}
      <Dialog open={showCopyStyleDialog} onOpenChange={setShowCopyStyleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy Style From Another Menu</DialogTitle>
            <DialogDescription>
              Select a menu to copy its design and display settings from
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Source Menu</Label>
              <Select value={copyStyleSourceId} onValueChange={setCopyStyleSourceId}>
                <SelectTrigger />
                <SelectContent>
                  {menus.filter(m => m.id !== selectedMenuId).map(menu => (
                    <SelectItem key={menu.id} value={menu.id}>
                      {menu.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyStyleDialog(false)}>Cancel</Button>
            <Button onClick={handleCopyStyle}>Copy Style</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Item Dialog */}
      <Dialog open={showCustomItemDialog} onOpenChange={setShowCustomItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCustomItemIndex !== null ? 'Edit' : 'Add'} Custom Item</DialogTitle>
            <DialogDescription>
              Add a custom link or button to your navigation menu
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Icon (Optional)</Label>
              <div className="grid grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => setCustomItem({...customItem, icon: null})}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    !customItem.icon
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="text-xs">No Icon</div>
                </button>
                {Object.entries(iconOptions).map(([key, IconComponent]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setCustomItem({...customItem, icon: key})}
                    className={`p-3 border-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                      customItem.icon === key
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <IconComponent />
                    <div className="text-xs capitalize">{key}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Label</Label>
              <Input
                value={customItem.label}
                onChange={(e) => setCustomItem({...customItem, label: e.target.value})}
                placeholder="e.g., My Account, Cart"
              />
              <p className="text-xs text-slate-500">Leave empty for icon-only display on desktop</p>
            </div>
            
            <div className="space-y-2">
              <Label>Mobile Label (Optional)</Label>
              <Input
                value={customItem.mobile_label || ''}
                onChange={(e) => setCustomItem({...customItem, mobile_label: e.target.value})}
                placeholder="e.g., Account, Shopping Cart"
              />
              <p className="text-xs text-slate-500">Text shown in mobile menu. Useful for icon-only items. Uses label if empty.</p>
            </div>
            
            {/* URL Type Selector */}
            <div className="space-y-2">
              <Label>URL Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCustomItem({...customItem, url_type: 'custom', url: '', category_id: null})}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    (!customItem.url_type || customItem.url_type === 'custom')
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="font-medium">Custom URL</div>
                  <div className="text-xs text-slate-500">Full URL</div>
                </button>
                <button
                  type="button"
                  onClick={() => setCustomItem({...customItem, url_type: 'category', url: '', category_id: null})}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    customItem.url_type === 'category'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="font-medium">Category URL</div>
                  <div className="text-xs text-slate-500">From category</div>
                </button>
              </div>
            </div>
            
            {/* URL Input or Category Selector */}
            {(!customItem.url_type || customItem.url_type === 'custom') ? (
              <div className="space-y-2">
                <Label>Custom URL</Label>
                <Input
                  value={customItem.url}
                  onChange={(e) => setCustomItem({...customItem, url: e.target.value})}
                  placeholder="https://example.com/page"
                />
                <p className="text-xs text-slate-500">Enter the full URL (e.g., https://example.com/page)</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Select Category</Label>
                <Select 
                  value={customItem.category_id || ''} 
                  onValueChange={(value) => {
                    // Flatten category hierarchy to find the selected category
                    const flattenCategories = (cats, result = []) => {
                      cats.forEach(cat => {
                        result.push(cat);
                        if (cat.children && cat.children.length > 0) {
                          flattenCategories(cat.children, result);
                        }
                      });
                      return result;
                    };
                    const allCategories = flattenCategories(categoryHierarchy);
                    const selectedCat = allCategories.find(c => c.id === value);
                    const catUrl = selectedCat?.url || (selectedCat?.slug ? `${currentMenu.root_domain || ''}/products-list/collections/${selectedCat.slug}` : '');
                    setCustomItem({...customItem, category_id: value, url: catUrl});
                  }}
                >
                  <SelectTrigger />
                  <SelectContent>
                    <SelectItem value="">Select a category</SelectItem>
                    {(() => {
                      // Flatten and display all categories
                      const flattenCategories = (cats, depth = 0, result = []) => {
                        cats.forEach(cat => {
                          result.push({ ...cat, depth });
                          if (cat.children && cat.children.length > 0) {
                            flattenCategories(cat.children, depth + 1, result);
                          }
                        });
                        return result;
                      };
                      return flattenCategories(categoryHierarchy).map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {'  '.repeat(cat.depth)}{cat.depth > 0 ? '└ ' : ''}{cat.name}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  URL will be: {currentMenu.root_domain || 'https://yoursite.com'}/products-list/collections/
                  {customItem.category_id ? (() => {
                    const flattenCategories = (cats) => {
                      let result = [];
                      cats.forEach(cat => {
                        result.push(cat);
                        if (cat.children) result = result.concat(flattenCategories(cat.children));
                      });
                      return result;
                    };
                    return flattenCategories(categoryHierarchy).find(c => c.id === customItem.category_id)?.slug || '[slug]';
                  })() : '[category-slug]'}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Position</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setCustomItem({...customItem, position: 'left'})}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    (!customItem.position || customItem.position === 'left')
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="font-medium">Left</div>
                  <div className="text-xs text-slate-500">← Side</div>
                </button>
                <button
                  type="button"
                  onClick={() => setCustomItem({...customItem, position: 'center'})}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    customItem.position === 'center'
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="font-medium">Center</div>
                  <div className="text-xs text-slate-500">↕ Middle</div>
                </button>
                <button
                  type="button"
                  onClick={() => setCustomItem({...customItem, position: 'right'})}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    customItem.position === 'right'
                      ? 'border-amber-500 bg-amber-50 text-amber-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="font-medium">Right</div>
                  <div className="text-xs text-slate-500">→ Side</div>
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={customItem.is_button}
                onChange={(e) => setCustomItem({...customItem, is_button: e.target.checked})}
                className="w-4 h-4"
              />
              <span className="text-sm">Style as button (highlighted/CTA style)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCustomItemDialog(false);
              setCustomItem({ label: '', url: '', url_type: 'custom', category_id: null, is_button: false, position: 'left', icon: null });
              setEditingCustomItemIndex(null);
            }}>Cancel</Button>
            <Button onClick={handleAddCustomItem}>
              {editingCustomItemIndex !== null ? 'Update' : 'Add'} Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Card Dialog */}
      <Dialog open={showCategoryCardDialog} onOpenChange={setShowCategoryCardDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCategoryCardId ? 'Edit' : 'Add'} Category Card</DialogTitle>
            <p className="text-sm text-slate-600">Configure a custom card for a specific category</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Category</Label>
              <select
                value={editingCategoryCardId || ''}
                onChange={(e) => setEditingCategoryCardId(e.target.value || null)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md mt-1"
                disabled={!!editingCategoryCardId}
              >
                <option value="">Choose a category...</option>
                {(currentMenu.included_categories || []).map(catId => {
                  const cat = categoryHierarchy.find(c => c.id === catId);
                  if (!cat) return null;
                  return (
                    <option key={catId} value={catId}>
                      {cat.name}
                    </option>
                  );
                })}
              </select>
            </div>
            
            {editingCategoryCardId && (() => {
              const cardData = currentMenu.column_specific_cards?.[editingCategoryCardId] || {
                title: '',
                description: '',
                button_text: '',
                button_url: '',
                image_url: '',
                image_layout: 'standard',
                image_height: 120,
                bg_color: '#f9fafb',
                title_color: '#1f2937',
                description_color: '#6b7280',
                button_bg_color: '#3b82f6',
                button_text_color: '#ffffff',
                button_hover_bg_color: '#2563eb',
                button_hover_text_color: '#ffffff',
                content_alignment: 'center'
              };
              
              return (
                <>
                  <div>
                    <Label>Card Image</Label>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setMediaBrowserCallback(() => (url) => {
                          const newCards = {...(currentMenu.column_specific_cards || {})};
                          newCards[editingCategoryCardId] = {...cardData, image_url: url};
                          setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                        });
                        setShowMediaBrowser(true);
                      }}
                      className="mt-1"
                    >
                      📁 Choose from Media
                    </Button>
                    {cardData.image_url && (
                      <div className="mt-2">
                        <img src={cardData.image_url} alt="Card" className="h-24 w-auto object-cover rounded border" />
                        <button
                          onClick={() => {
                            const newCards = {...(currentMenu.column_specific_cards || {})};
                            newCards[editingCategoryCardId] = {...cardData, image_url: ''};
                            setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                          }}
                          className="text-red-600 text-sm mt-1 hover:underline"
                        >
                          Remove Image
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Image Layout</Label>
                      <select
                        value={cardData.image_layout || 'standard'}
                        onChange={(e) => {
                          const newCards = {...(currentMenu.column_specific_cards || {})};
                          newCards[editingCategoryCardId] = {...cardData, image_layout: e.target.value};
                          setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                        }}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mt-1"
                      >
                        <option value="standard">Standard</option>
                        <option value="cover">Cover</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Content Alignment</Label>
                      <select
                        value={cardData.content_alignment || 'center'}
                        onChange={(e) => {
                          const newCards = {...(currentMenu.column_specific_cards || {})};
                          newCards[editingCategoryCardId] = {...cardData, content_alignment: e.target.value};
                          setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                        }}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mt-1"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                  </div>
                  
                  <Input
                    placeholder="Card Title"
                    value={cardData.title || ''}
                    onChange={(e) => {
                      const newCards = {...(currentMenu.column_specific_cards || {})};
                      newCards[editingCategoryCardId] = {...cardData, title: e.target.value};
                      setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                    }}
                  />
                  
                  <Textarea
                    placeholder="Card Description"
                    value={cardData.description || ''}
                    onChange={(e) => {
                      const newCards = {...(currentMenu.column_specific_cards || {})};
                      newCards[editingCategoryCardId] = {...cardData, description: e.target.value};
                      setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                    }}
                    rows={3}
                  />
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Button Text"
                      value={cardData.button_text || ''}
                      onChange={(e) => {
                        const newCards = {...(currentMenu.column_specific_cards || {})};
                        newCards[editingCategoryCardId] = {...cardData, button_text: e.target.value};
                        setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                      }}
                    />
                    <Input
                      placeholder="Button URL"
                      value={cardData.button_url || ''}
                      onChange={(e) => {
                        const newCards = {...(currentMenu.column_specific_cards || {})};
                        newCards[editingCategoryCardId] = {...cardData, button_url: e.target.value};
                        setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                      }}
                    />
                  </div>
                  
                  <div className="pt-3 border-t">
                    <Label className="text-sm font-medium">Colors</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <Label className="text-xs">Background</Label>
                        <Input
                          type="color"
                          value={cardData.bg_color || '#f9fafb'}
                          onChange={(e) => {
                            const newCards = {...(currentMenu.column_specific_cards || {})};
                            newCards[editingCategoryCardId] = {...cardData, bg_color: e.target.value};
                            setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Title Color</Label>
                        <Input
                          type="color"
                          value={cardData.title_color || '#1f2937'}
                          onChange={(e) => {
                            const newCards = {...(currentMenu.column_specific_cards || {})};
                            newCards[editingCategoryCardId] = {...cardData, title_color: e.target.value};
                            setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Description Color</Label>
                        <Input
                          type="color"
                          value={cardData.description_color || '#6b7280'}
                          onChange={(e) => {
                            const newCards = {...(currentMenu.column_specific_cards || {})};
                            newCards[editingCategoryCardId] = {...cardData, description_color: e.target.value};
                            setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Button Background</Label>
                        <Input
                          type="color"
                          value={cardData.button_bg_color || '#3b82f6'}
                          onChange={(e) => {
                            const newCards = {...(currentMenu.column_specific_cards || {})};
                            newCards[editingCategoryCardId] = {...cardData, button_bg_color: e.target.value};
                            setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Button Text Color</Label>
                        <Input
                          type="color"
                          value={cardData.button_text_color || '#ffffff'}
                          onChange={(e) => {
                            const newCards = {...(currentMenu.column_specific_cards || {})};
                            newCards[editingCategoryCardId] = {...cardData, button_text_color: e.target.value};
                            setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Button Hover BG</Label>
                        <Input
                          type="color"
                          value={cardData.button_hover_bg_color || '#2563eb'}
                          onChange={(e) => {
                            const newCards = {...(currentMenu.column_specific_cards || {})};
                            newCards[editingCategoryCardId] = {...cardData, button_hover_bg_color: e.target.value};
                            setCurrentMenu(prev => ({...prev, column_specific_cards: newCards}));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCategoryCardDialog(false);
              setEditingCategoryCardId(null);
            }}>Cancel</Button>
            <Button onClick={() => {
              setShowCategoryCardDialog(false);
              setEditingCategoryCardId(null);
              handleSave();
            }} disabled={!editingCategoryCardId}>
              Save Card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mega Menu Column Editor Modal */}
      {showColumnEditor && (
        <MegaMenuColumnEditor
          column={editingColumn}
          categories={categoryHierarchy}
          menuConfig={currentMenu}
          onBrowseMedia={(callback) => {
            setMediaBrowserCallback(() => callback);
            setShowMediaBrowser(true);
          }}
          onSave={(updatedColumn) => {
            const rows = [...(currentMenu.mega_menu_rows || [])];
            if (rows[editingRowIndex] && rows[editingRowIndex].columns) {
              rows[editingRowIndex].columns[editingColIndex] = updatedColumn;
              setCurrentMenu({...currentMenu, mega_menu_rows: rows});
            }
          }}
          onClose={() => {
            setShowColumnEditor(false);
            setEditingColumn(null);
            setEditingRowIndex(null);
            setEditingColIndex(null);
          }}
        />
      )}
      
      {/* Media Browser - rendered at top level */}
      <MediaBrowser
        isOpen={showMediaBrowser}
        onClose={() => {
          setShowMediaBrowser(false);
          setMediaBrowserCallback(null);
        }}
        onSelect={(url) => {
          if (mediaBrowserCallback) {
            mediaBrowserCallback(url);
          }
          setShowMediaBrowser(false);
          setMediaBrowserCallback(null);
        }}
        allowMultiple={false}
      />
    </div>
  );
};

export default NavigationBuilder;
